/**
 * Kit Builder -- Vanilla JS engine -- v7.32 -- 2026 06 22
 * Compatible with Shopify Focal theme (OS2.0)
 * No external dependencies. ES5-compatible.
 *
 * v4.0 - OOS overlay; tag-based grouping; block-based ordering; split-colour swatches
 * v4.3 - Horizontal split gradient (accent top / silver bottom); metallic sheen on all swatches;
 *         Chrome Top added; mobile-aware getOptimalCols (3 cols for <=3 items on mobile)
 * v5.0 - Icon picker (kit-icon products); logo uploader (kit-logo-upload product);
 *         char limit 25; dynamic banner text scaling; banner icon+text+logo slots
 * v7.25 - Stamp _kit_addon:'icon'/'logo' on addon line items for packing slip identification
 * v7.26 - Generate base64 thumbnail (_kit_logo_thumb) at cart time for packing slip rendering
 * v7.27 - Fix _kit_addon stamping to use product ID instead of tag lookup (icon was not matching);
 *         Reduce thumbnail to 80x53 q0.15 with 4000-char cap; stamp only on logo item to avoid
 *         Shopify silently dropping oversized properties on all items
 * v7.28 - Rename _kit_logo_thumb -> __kit_logo_thumb (double-underscore = hidden from invoice/customer views)
 * v7.29 - Fix thumbnail generation: white background fill (transparent SVG -> visible JPEG);
 *         PNG output for SVG sources (cleaner logos); handle zero-dimension SVG intrinsic size;
 *         packing slip now renders via canvas+createImageBitmap to bypass img-src CSP
 * v7.30 - Add Cloudflare Worker integration: after Cloudinary upload, call LOGO_CDN_WORKER_URL
 *         to upload logo to Shopify CDN; stamp _kit_logo_cdn_url on cart; packing slip
 *         renders <img src> using cdn.shopify.com URL (bypasses Order Printer CSP)
 * v7.32 - Font buttons: add !important to inline font-family/style/weight on label span
 *         to override Shopify theme CSS that was preventing custom fonts from rendering
 */
(function () {
  'use strict';

  // -- SPLIT-COLOUR MAP -- must be at the top of the IIFE ---------------------
  // kit-builder.js is loaded with `defer`, so the DOM is already ready when
  // the script executes. The bootstrap block calls init() *synchronously*,
  // before execution reaches the bottom of the file. Any var *assignment*
  // (not a function declaration) used inside init() -> renderColours() must
  // therefore be initialised here, at the top, not at the bottom.
  var SPLIT_COLOURS = {
    'blue top':   '#0D20CF',   // deep saturated blue (was #4169E1)
    'gold top':   '#D4AF30',   // warm vivid gold (was #CFB53B)
    'copper top': '#C06A18',   // vibrant orange-copper (was #B87333)
    'chrome top': '#8898B8'    // steel-blue chrome accent on top, silver below
  };

  // -- METALLIC GRADIENT MAP -- multi-stop gradients for known metallic finishes --
  // Returns a CSS gradient string used as the swatch dot background.
  // Each gradient simulates a highlight-to-shadow metallic sweep (top -> bottom).
  var METALLIC_GRADIENTS = {
    'gold':          'linear-gradient(to bottom, #F0D868 0%, #D4AF30 30%, #A88C18 50%, #D4AF30 70%, #ECC840 100%)',
    'chrome':        'linear-gradient(to bottom, #eaf0f8 0%, #c8d8e8 30%, #a8b8cc 50%, #c8d8e8 70%, #dce8f4 100%)',
    'copper':        'linear-gradient(to bottom, #DC8838 0%, #C06A18 30%, #8A4808 50%, #C06A18 70%, #D08030 100%)',
    'gloss black':   'linear-gradient(to bottom, #484848 0%, #1a1a1a 25%, #080808 50%, #1a1a1a 75%, #383838 100%)',
    'metallic blue': 'linear-gradient(to bottom, #2A40F5 0%, #0D20CF 30%, #0810A0 50%, #0D20CF 70%, #1830E8 100%)'
  };

  // -- COLOUR FAMILIES -------------------------------------------------------
  // Maps a family key (used in kit-colour-* tags and state.selectedColour)
  // to: display label, swatch CSS key, banner hex, and the variant option
  // values that belong to this family.
  //
  // The ORDER of keys here controls the order of swatches in Step 1.
  // swatchKey must be a key in SPLIT_COLOURS or METALLIC_GRADIENTS (or a plain
  // hex string); it is passed straight to colourNameToSwatchCSS().
  // bannerHex is used for the solid colour banner below the image frame.
  // 8 individual colour swatches, each with a group ('full' or 'top').
  //
  // Tagging rules (apply to product in Shopify):
  //   kit-colour-full  -> appears in ALL full-colour kits (gold, copper, blue, black)
  //   kit-colour-top   -> appears in ALL top-colour kits (gold-top, copper-top, blue-top, chrome-top)
  //   kit-colour-{key} -> appears in ONE specific kit (exact override, combinable with above)
  //   kit-colour-agnostic -> appears in all 8 kits regardless of colour
  //
  // Examples:
  //   Tweezers in every finish  -> kit-colour-full + kit-colour-top
  //   Gold-top spoons (want in gold kit too) -> kit-colour-top + kit-colour-gold
  //   Gold-only item -> kit-colour-gold
  //
  // Swatch dot: solid metallic gradient for full-colour; split (accent|silver) for -top.
  var COLOUR_FAMILIES = [
    { key: 'black',      group: 'full', label: 'Gloss Black', swatchKey: 'gloss black',   bannerHex: '#1a1a1a', bannerGradient: 'linear-gradient(150deg, #484848 0%, #1a1a1a 35%, #080808 55%, #1a1a1a 75%, #282828 100%)', variants: ['gloss black'] },
    { key: 'chrome-top', group: 'top',  label: 'Chrome Top',  swatchKey: 'chrome top',    bannerHex: '#C0D0DE', bannerGradient: 'linear-gradient(150deg, #E8EEF5 0%, #C8D8E8 35%, #A8B8CC 55%, #C8D8E8 75%, #D8E4F2 100%)', variants: ['chrome top', 'chrome'] },
    { key: 'copper-top', group: 'top',  label: 'Copper Top',  swatchKey: 'copper top',    bannerHex: '#C06A18', bannerGradient: 'linear-gradient(150deg, #D08030 0%, #C06A18 30%, #DC8838 50%, #8A4808 70%, #C06A18 100%)', variants: ['copper top'] },
    { key: 'copper',     group: 'full', label: 'Copper',      swatchKey: 'copper',        bannerHex: '#C06A18', bannerGradient: 'linear-gradient(150deg, #DC8838 0%, #C06A18 35%, #8A4808 55%, #C06A18 75%, #D08030 100%)', variants: ['copper'] },
    { key: 'gold-top',   group: 'top',  label: 'Gold Top',    swatchKey: 'gold top',      bannerHex: '#D4AF30', bannerGradient: 'linear-gradient(150deg, #ECC840 0%, #D4AF30 30%, #F0D060 50%, #A88C18 70%, #D4AF30 100%)', variants: ['gold top'] },
    { key: 'gold',       group: 'full', label: 'Gold',        swatchKey: 'gold',          bannerHex: '#D4AF30', bannerGradient: 'linear-gradient(150deg, #F0D060 0%, #D4AF30 35%, #A88C18 55%, #D4AF30 75%, #ECC840 100%)', variants: ['gold'] },
    { key: 'blue-top',   group: 'top',  label: 'Blue Top',    swatchKey: 'blue top',      bannerHex: '#0D20CF', bannerGradient: 'linear-gradient(150deg, #1830E8 0%, #0D20CF 30%, #2840F5 50%, #0810A0 70%, #0D20CF 100%)', variants: ['blue top'] },
    { key: 'blue',       group: 'full', label: 'Metallic Blue', swatchKey: 'metallic blue', bannerHex: '#0D20CF', bannerGradient: 'linear-gradient(150deg, #2840F5 0%, #0D20CF 35%, #0810A0 55%, #0D20CF 75%, #1830E8 100%)', variants: ['metallic blue'] }
  ];

  // -- COLOUR AFFINITY -------------------------------------------------------
  // When a product is included via a group tag (kit-colour-full / kit-colour-top),
  // the affinity list determines which variant values "count" for each kit.
  // A product is only included in the kit if it has at least one variant whose
  // colour option value appears in that kit's affinity list.
  //
  // This lets gold-top spoons (tagged kit-colour-full) appear in the gold kit
  // (because 'gold top' is in the gold affinity list) but NOT in the copper or
  // blue kits (no copper- or blue-related variant). No extra tags needed.
  var COLOUR_AFFINITY = {
    'gold':       ['gold', 'gold top'],
    'gold-top':   ['gold top', 'gold'],
    'copper':     ['copper', 'copper top'],
    'copper-top': ['copper top', 'copper'],
    'blue':       ['metallic blue', 'blue top'],
    'blue-top':   ['blue top', 'metallic blue'],
    'chrome-top': ['chrome top', 'chrome'],
    'black':      ['gloss black']
  };

  // -- RESERVED TAG SUFFIXES -------------------------------------------------
  // These kit-* tag suffixes carry behaviour flags, NOT category names.
  // getKitCategory() also excludes any suffix starting with 'colour-' so that
  // kit-colour-gold etc. don't create phantom category tabs.
  // 'icon' and 'logo-upload' are add-on products shown only in step 3, not the product grid.
  var RESERVED_TAGS = ['eligible', 'colour-agnostic', 'show-variants', 'icon', 'logo-upload', 'full', 'top'];

  // -- ICON IMAGE FALLBACK ---------------------------------------------------
  // Exposed as a global so it can be called from onerror HTML attributes.
  // Falls through a pipe-separated chain stored in data-icon-chain:
  //   src = kit-icon-[handle].svg  (preferred)
  //   chain[0] = kit-icon-[handle].png
  //   chain[1] = product.image (Shopify CDN)
  window._kbIconFallback = function (img) {
    var chain = img.getAttribute('data-icon-chain') || '';
    var parts = chain.split('|');
    var next = parts.shift();
    if (next) {
      img.setAttribute('data-icon-chain', parts.join('|'));
      img.src = next;
    } else {
      img.onerror = null;
    }
  };

  // -- STEP INSTRUCTIONS ------------------------------------------------------
  // Short subtitle shown above the progress bar (between page title and nav).
  // Updated by goToStep(). Initial value is set in the Liquid as '1. Choose Your Colour'.
  var STEP_INSTRUCTIONS = [
    '',                                      // index 0 unused
    '1. Choose Your Colour',
    '2. Choose Your Products',
    '3. Personalise Your Kit (Optional)',
    '4. Review Your Kit'
  ];

  // -- STEP ANNOUNCEMENTS --------------------------------------------------------
  // Small secondary text shown beneath the step instruction for relevant steps.
  var STEP_ANNOUNCEMENTS = [
    '',
    '',
    '',
    'Our skilled engravers will resize and align your text, icon and logo perfectly for every tool.',
    ''
  ];

  // -- CLOUDINARY ------------------------------------------------------------
  // Unsigned upload preset -- no API secret required for browser-side uploads.
  var CLOUDINARY_UPLOAD_URL = 'https://api.cloudinary.com/v1_1/dgsldher1/image/upload';
  var CLOUDINARY_PRESET     = 'oui-kit-logos';

  // -- SHOPIFY CDN LOGO WORKER -----------------------------------------------
  // Cloudflare Worker URL that forwards the logo to Shopify Files (cdn.shopify.com).
  // Required for the logo to display in Order Printer packing slips (CSP restriction).
  // Set to '' to disable; packing slip will show a placeholder + download link instead.
  // Replace with your own deployed Worker URL after following the installation guide.
  var LOGO_CDN_WORKER_URL = '';

  // -- STATE ------------------------------------------------------------------

  var state = {
    step: 1,
    selectedColour: null,
    // Default to first block category, then legacy defaultCategory setting, then 'all'
    selectedCategory: (
      window.KitBuilder && window.KitBuilder.categories && window.KitBuilder.categories.length > 0
        ? window.KitBuilder.categories[0].tagSuffix
        : ((window.KitBuilder && window.KitBuilder.settings.defaultCategory) || 'all')
    ),
    kitItems: [],
    pendingVariantSelections: {}, // { productId: { heightVal, piecesVal } } for matrix cards
    activePillVariantPerProduct: {}, // { productId: variantId } -- which variant the shared stepper controls
    engraving: {
      text: '',
      fontId: 'serif',
      iconProductId: null,
      iconVariantId: null,
      logoDataUrl:   null,
      logoFileName:  null,
      logoCloudUrl:    null,    // Cloudinary permanent URL (stored on line item)
      logoThumbUrl:    null,    // base64 data URL thumbnail (fallback, 80x53)
      logoUploading:   false,   // true while Cloudinary upload is in flight
      logoCdnUrl:      null,    // cdn.shopify.com URL uploaded by Cloudflare Worker (packing slip img)
      logoCdnUploading: false,  // true while Worker upload is in flight (background; non-blocking)
      logoProductId: null,
      logoVariantId: null
    },
    kitId: null,
    adding: false
  };

  // -- BOOTSTRAP -------------------------------------------------------------

  // -- PRODUCT CACHE ----------------------------------------------------------
  // Invalidated in selectColour(); rebuilt on first getProductsForColour() call.
  var _cachedColourKey      = null;
  var _cachedProducts       = null;

  function init() {
    if (!window.KitBuilder || !window.KitBuilder.products) {
      console.warn('[KitBuilder] No product data found.');
      return;
    }
    normaliseTags();  // pre-lowercase tags so hasTag() uses indexOf, not some+toLowerCase
    state.kitId = generateKitId();
    initStickyOffset();
    updatePickerHeight();
    initEngravedBannerViewportFix();
    window.addEventListener('resize', debounce(function () {
      initStickyOffset();
      updatePickerHeight();
      refreshEngravedBanner(); // re-measure text slot on viewport width change
    }, 150));
    renderColours();
    bindGlobalEvents();
  }

  /**
   * Pre-processes product tags to lowercase trimmed strings once at init.
   * Stored on product._tagsLower so hasTag() does a fast indexOf lookup
   * instead of calling toLowerCase() on every tag on every check.
   */
  function normaliseTags() {
    window.KitBuilder.products.forEach(function (p) {
      p._tagsLower = p.tags
        ? p.tags.map(function (t) { return t.toLowerCase().trim(); })
        : [];
    });
  }

  /**
   * Finds the fixed/sticky theme header once, measures its full height, then
   * wires up a scroll listener that snaps --kb-sticky-offset between two cached
   * values: full header height (header visible) or 0 (header scrolled away).
   *
   * Snapping avoids the per-frame getBoundingClientRect() calls that were causing
   * jitter on iOS Safari: when the Focal header slides out with a CSS transition,
   * reading rect.bottom on every RAF frame produced a continuously-changing top
   * value on the sticky picker-header, making it visibly shake.
   *
   * Called once at init and again on resize (to re-measure if viewport changes).
   */
  function initStickyOffset() {
    // Set --kb-sticky-offset on the .kit-builder ROOT element so it is inherited
    // by ALL descendants: both #kb-image-col (engraving banner, step 3) and
    // .kit-builder__picker-header (fixed category bar, step 2). Setting it only
    // on #kb-image-col meant the picker-header -- a sibling's descendant -- never
    // received the value and always fell back to 0px.
    var kbEl = document.querySelector('.kit-builder');
    var col = kbEl; // alias used in applyOffset below
    if (!col) return;

    // Focal/Dawn header selectors in priority order.
    var selectors = [
      'sticky-header',
      '#shopify-section-header .header-wrapper',
      '#shopify-section-header',
      '.shopify-section-group--header-group',
      'header.header',
      '.header-wrapper',
      '#announcement-bar',
      '.announcement-bar-section',
      '.shopify-section-group--header-group sticky-header'
    ];

    var headerEl = null;
    var cachedHeight = 0;

    selectors.forEach(function (sel) {
      if (headerEl) return; // stop at first match
      var el = document.querySelector(sel);
      if (!el) return;
      var pos = window.getComputedStyle(el).position;
      if (pos !== 'fixed' && pos !== 'sticky') return;
      var rect = el.getBoundingClientRect();
      if (rect.height > 0) {
        headerEl = el;
        cachedHeight = rect.height;
      }
    });

    // No fixed/sticky header found -- offset stays 0.
    if (!headerEl) {
      col.style.setProperty('--kb-sticky-offset', '0px');
      return;
    }

    function applyOffset() {
      // Snap: header fully visible (top near 0) -> use cachedHeight, else 0.
      // Using a simple threshold check rather than reading rect.bottom during
      // the CSS transition, which caused per-frame jitter on iOS Safari.
      var rect = headerEl.getBoundingClientRect();
      var visible = rect.bottom > cachedHeight * 0.5;
      col.style.setProperty('--kb-sticky-offset', visible ? cachedHeight + 'px' : '0px');
    }

    applyOffset(); // set immediately

    // Debounced scroll listener -- fires 50ms AFTER scrolling activity stops,
    // NOT on every scroll frame.
    //
    // Why debounce instead of RAF? On iOS Safari, any JS-driven change to a CSS
    // property that controls a sticky element's top position causes a visual
    // stutter during momentum scroll: the browser hands off momentum scrolling to
    // the GPU asynchronously, and a mid-scroll DOM write interrupts that async
    // path, making the sticky element flicker. By debouncing to 50ms we never
    // touch the DOM while the user is actively scrolling, so the sticky element
    // stays perfectly smooth during the scroll and corrects once it stops.
    var _debounceId = null;
    window.addEventListener('scroll', function () {
      clearTimeout(_debounceId);
      _debounceId = setTimeout(applyOffset, 50);
    }, { passive: true });
  }

  /** Legacy alias kept so any external callers still work. */
  function updateStickyOffset() { initStickyOffset(); }

  /**
   * iOS Safari Visual Viewport fix for the fixed engraving banner (step 3).
   *
   * When the virtual keyboard opens, iOS scrolls the VISUAL viewport relative
   * to the layout viewport. position:fixed is anchored to the layout viewport,
   * so without compensation the banner appears to "scroll away" as the visual
   * viewport shifts. window.visualViewport.offsetTop exposes the scroll amount;
   * we add it to the banner's top so it stays visually fixed during keyboard entry.
   *
   * We also clear the inline top on step changes so the CSS rule takes over
   * on other steps (desktop sticky, colour step, etc.).
   */
  function initEngravedBannerViewportFix() {
    if (!window.visualViewport) return;
    var imageCol = document.getElementById('kb-image-col');
    if (!imageCol) return;

    function applyFix() {
      if (window.innerWidth >= 768) return;
      if (imageCol.dataset.activeStep !== '3') return;
      var kbEl = document.querySelector('.kit-builder');
      var base = kbEl
        ? (parseFloat(kbEl.style.getPropertyValue('--kb-sticky-offset')) || 0)
        : 0;
      imageCol.style.top = (base + (window.visualViewport.offsetTop || 0)) + 'px';
    }

    function clearFix() {
      imageCol.style.removeProperty('top');
    }

    window.visualViewport.addEventListener('resize', function () {
      if (imageCol.dataset.activeStep === '3' && window.innerWidth < 768) applyFix();
      else clearFix();
    }, { passive: true });

    window.visualViewport.addEventListener('scroll', function () {
      if (imageCol.dataset.activeStep === '3' && window.innerWidth < 768) applyFix();
      else clearFix();
    }, { passive: true });
  }

  /**
   * Measures the rendered engraving banner height after step 3 activates and
   * writes it to --kb-banner-height on .kit-builder so the content-col's
   * margin-top exactly matches the actual banner, preventing the browser from
   * scrolling to reveal inputs that appear partially hidden behind the banner.
   */
  function updateEngravedBannerMargin() {
    if (window.innerWidth >= 768) return;
    var imageCol = document.getElementById('kb-image-col');
    var kbEl = document.querySelector('.kit-builder');
    if (!imageCol || !kbEl) return;
    requestAnimationFrame(function () {
      var h = imageCol.getBoundingClientRect().height;
      if (h > 0) kbEl.style.setProperty('--kb-banner-height', h + 'px');
    });
  }

  /**
   * Measures the rendered height of the fixed picker-header and writes it to
   * --kb-picker-height on .kit-builder so the spacer div below it has the
   * same height, preventing the product grid from sliding behind the header.
   * Only runs on mobile (>= 768px: picker-header is not fixed).
   */
  function updatePickerHeight() {
    if (window.innerWidth >= 768) return;
    var header = document.querySelector('.kit-builder__picker-header');
    var kbEl = document.querySelector('.kit-builder');
    if (!header || !kbEl) return;
    requestAnimationFrame(function () {
      var h = header.getBoundingClientRect().height;
      if (h > 0) kbEl.style.setProperty('--kb-picker-height', h + 'px');
    });
  }

  /** Lightweight debounce for resize handler. */
  function debounce(fn, ms) {
    var t;
    return function () {
      clearTimeout(t);
      t = setTimeout(fn, ms);
    };
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // -- COLOUR STEP -----------------------------------------------------------

  function renderColours() {
    var families = getAvailableFamilies();
    var grid = document.getElementById('kb-colour-grid');
    if (!grid) return;

    if (families.length === 0) {
      grid.innerHTML =
        '<p class="kit-builder__empty">No colours found. ' +
        'Tag your products with kit-colour-full, kit-colour-top, or kit-colour-{colour} and check the collection handle.</p>';
      return;
    }

    grid.innerHTML = families.map(function (family) {
      var swatchBg = colourNameToSwatchCSS(family.swatchKey);
      var oos      = isFamilyFullyOOS(family.key);
      return (
        '<button class="kit-builder__colour-swatch' + (oos ? ' is-oos' : '') + '" ' +
          'data-colour="' + esc(family.key) + '" ' +
          'role="listitem" ' +
          'aria-label="Select ' + esc(family.label) + (oos ? ' -- sold out' : '') + '" ' +
          'aria-pressed="false"' + (oos ? ' disabled' : '') + '>' +
          '<span class="kit-builder__colour-dot" style="background:' + swatchBg + ';" aria-hidden="true"></span>' +
          '<span class="kit-builder__colour-name">' + esc(family.label) + '</span>' +
        '</button>'
      );
    }).join('');

    grid.querySelectorAll('.kit-builder__colour-swatch:not([disabled])').forEach(function (btn) {
      btn.addEventListener('click', function () {
        selectColour(this.dataset.colour);
      });
    });
  }

  /**
   * Returns true if every non-agnostic product for this colour family has
   * all variants out of stock. Used to mark swatches as sold out so the
   * customer knows the colour exists but is temporarily unavailable.
   */
  function isFamilyFullyOOS(familyKey) {
    var products = getProductsForColour(familyKey).filter(function (p) {
      return !hasTag(p, 'kit-colour-agnostic');
    });
    if (products.length === 0) return false;
    return products.every(function (p) {
      return p.variants.every(function (v) { return !v.available; });
    });
  }

  /**
   * Returns the COLOUR_FAMILIES entry for a given family key, or null.
   */
  function getFamilyByKey(key) {
    for (var i = 0; i < COLOUR_FAMILIES.length; i++) {
      if (COLOUR_FAMILIES[i].key === key) return COLOUR_FAMILIES[i];
    }
    return null;
  }

  /**
   * Returns the subset of COLOUR_FAMILIES that have at least one product
   * that would appear in that kit. Mirrors the inclusion logic of getProductsForColour
   * so swatches are shown for group-tagged (kit-colour-full / kit-colour-top) products
   * as well as exact-tagged ones. Preserves COLOUR_FAMILIES order.
   */
  function getAvailableFamilies() {
    var optName = (window.KitBuilder.settings.colourOptionName || 'Color').toLowerCase().trim();
    return COLOUR_FAMILIES.filter(function (family) {
      var affinity = COLOUR_AFFINITY[family.key] || family.variants;
      return window.KitBuilder.products.some(function (p) {
        if (hasTag(p, 'kit-icon'))        return false;
        if (hasTag(p, 'kit-logo-upload')) return false;
        if (hasTag(p, 'kit-colour-agnostic'))     return false; // agnostic doesn't drive swatch display
        if (hasTag(p, 'kit-colour-' + family.key)) return true; // exact tag
        if (family.group && hasTag(p, 'kit-colour-' + family.group)) {
          // Group tag: only counts if the product has a variant in this colour's affinity
          return p.variants.some(function (v) {
            var val = getColourValue(v, p, optName);
            return val && affinity.indexOf(val.toLowerCase()) >= 0;
          });
        }
        return false;
      });
    });
  }

  /**
   * Returns all variants of a product whose colour option value belongs to
   * the given colour family (case-insensitive). Returns [] for agnostic products.
   */
  function getVariantsForFamily(product, familyKey) {
    var family = getFamilyByKey(familyKey);
    if (!family) return [];
    var optName = (window.KitBuilder.settings.colourOptionName || 'Color').toLowerCase().trim();
    return product.variants.filter(function (v) {
      var val = getColourValue(v, product, optName);
      return val && family.variants.indexOf(val.toLowerCase()) >= 0;
    });
  }

  /**
   * When a product is included via a group/affinity tag (not an exact family tag),
   * it may have no exact-colour variant (e.g. palette knife with only 'gold top'
   * showing in the gold kit). This function finds the best variant to display by
   * checking the COLOUR_AFFINITY list -- prefers available, falls back to any.
   * Returns null if no affinity variant exists (caller then uses getDefaultVariant).
   */
  function getBestAffinityVariant(product, familyKey) {
    var affinity = COLOUR_AFFINITY[familyKey] || [];
    if (!affinity.length) return null;
    var optName = (window.KitBuilder.settings.colourOptionName || 'Color').toLowerCase().trim();
    return (
      product.variants.find(function (v) {
        var val = getColourValue(v, product, optName);
        return val && affinity.indexOf(val.toLowerCase()) >= 0 && v.available;
      }) ||
      product.variants.find(function (v) {
        var val = getColourValue(v, product, optName);
        return val && affinity.indexOf(val.toLowerCase()) >= 0;
      }) ||
      null
    );
  }

  function selectColour(colour) {
    state.selectedColour = colour;
    state.kitItems = [];
    state.pendingVariantSelections = {};
    state.activePillVariantPerProduct = {};
    state.kitId = generateKitId();
    state.selectedCategory = getDefaultCategory();
    // Invalidate product cache -- new colour = different product set
    _cachedColourKey = null;
    _cachedProducts  = null;

    document.querySelectorAll('.kit-builder__colour-swatch').forEach(function (btn) {
      var selected = btn.dataset.colour === colour;
      btn.classList.toggle('is-selected', selected);
      btn.setAttribute('aria-pressed', selected ? 'true' : 'false');
    });

    // Show + colour the banner below the image frame
    updateColourBanner();
    // Re-measure banner text slot after the banner becomes visible.
    // The banner is hidden until a colour is selected, so any earlier measurement
    // used a slotWidth fallback. Double-rAF lets the browser paint the new visible
    // banner before we measure textEl.offsetWidth and recalculate font size.
    requestAnimationFrame(function () {
      requestAnimationFrame(refreshEngravedBanner);
    });

    goToStep(2);
    renderProducts();
    renderMosaic();
    updateNextButton();
    updateImageColState();
  }

  // -- PRODUCT STEP ----------------------------------------------------------

  /**
   * Returns all products visible for the chosen colour FAMILY KEY.
   * Result is cached per family key -- invalidated when colour changes (selectColour).
   * Excludes add-on products (kit-icon, kit-logo-upload) -- shown in step 3 only.
   * OOS products are included so they can be shown with the OOS overlay.
   *
   * Inclusion rules (first match wins, checked in order):
   *   1. kit-colour-agnostic -> always included
   *   2. kit-colour-{familyKey} -> included (exact single-colour override)
   *   3. kit-colour-full / kit-colour-top -> included if this family's group matches
   *      AND the product has at least one variant in this kit's COLOUR_AFFINITY list.
   *      This is the key: gold-top spoons tagged kit-colour-full appear in the gold
   *      kit (gold top is in gold's affinity) but NOT copper or blue (no match).
   */
  function getProductsForColour(familyKey) {
    if (_cachedColourKey === familyKey && _cachedProducts !== null) {
      return _cachedProducts;
    }
    _cachedColourKey = familyKey;
    var family   = getFamilyByKey(familyKey);
    var group    = family ? family.group : null;
    var affinity = COLOUR_AFFINITY[familyKey] || (family ? family.variants : []);
    var optName  = (window.KitBuilder.settings.colourOptionName || 'Color').toLowerCase().trim();

    _cachedProducts = window.KitBuilder.products.filter(function (product) {
      if (hasTag(product, 'kit-icon'))        return false;
      if (hasTag(product, 'kit-logo-upload')) return false;
      if (hasTag(product, 'kit-colour-agnostic'))     return true;
      if (hasTag(product, 'kit-colour-' + familyKey)) return true;
      // Group tag: only include if the product has a variant related to this colour
      if (group && hasTag(product, 'kit-colour-' + group)) {
        return product.variants.some(function (v) {
          var val = getColourValue(v, product, optName);
          return val && affinity.indexOf(val.toLowerCase()) >= 0;
        });
      }
      return false;
    });
    return _cachedProducts;
  }

  /**
   * Returns products tagged kit-icon (icon add-on products).
   */
  function getIconProducts() {
    return window.KitBuilder.products.filter(function (p) { return hasTag(p, 'kit-icon'); });
  }

  /**
   * Returns the first product tagged kit-logo-upload (logo charge product), or null.
   */
  function getLogoProduct() {
    return window.KitBuilder.products.find(function (p) { return hasTag(p, 'kit-logo-upload'); }) || null;
  }

  function getColourValue(variant, product, optName) {
    var idx = -1;
    product.options.forEach(function (opt, i) {
      if (opt.name.toLowerCase().trim() === optName) idx = i;
    });
    if (idx < 0) return null;
    return variant['option' + (idx + 1)];
  }

  /**
   * Returns the best variant for this colour: prefers available, falls back to first OOS.
   */
  function getVariantForColour(product, colour) {
    var optName = (window.KitBuilder.settings.colourOptionName || 'Color').toLowerCase().trim();
    return (
      product.variants.find(function (v) {
        return getColourValue(v, product, optName) === colour && v.available;
      }) ||
      product.variants.find(function (v) {
        return getColourValue(v, product, optName) === colour;
      }) ||
      product.variants[0]
    );
  }

  /**
   * Returns the best default variant for a colour-agnostic product
   * (no colour option to match against -- prefer first available, else first overall).
   */
  function getDefaultVariant(product) {
    return product.variants.find(function (v) { return v.available; }) || product.variants[0];
  }

  // -- TAG-BASED CATEGORY HELPERS --------------------------------------------

  /**
   * Returns true if a product has the given tag (case-insensitive).
   * Uses pre-normalised _tagsLower array (built in normaliseTags at init)
   * for a fast indexOf check instead of some+toLowerCase on every call.
   */
  function hasTag(product, tagName) {
    var tags = product._tagsLower || [];
    return tags.indexOf(tagName.toLowerCase().trim()) >= 0;
  }

  /**
   * Returns the kit category suffix for a product (first kit-* tag that is
   * neither a reserved behaviour flag nor a colour-family tag).
   * e.g. tag "kit-tweezers" -> "tweezers"
   * Skips: kit-eligible, kit-colour-agnostic, kit-show-variants, kit-colour-*,
   *        kit-icon, kit-logo-upload
   */
  function getKitCategory(product) {
    if (!product.tags || !product.tags.length) return null;
    for (var i = 0; i < product.tags.length; i++) {
      var tag = product.tags[i].toLowerCase().trim();
      if (tag.indexOf('kit-') === 0) {
        var suffix = tag.slice(4);
        if (RESERVED_TAGS.indexOf(suffix) < 0 && suffix.indexOf('colour-') !== 0) {
          return suffix;
        }
      }
    }
    return null;
  }

  /**
   * Auto-detect categories from product tags when no blocks are defined.
   * Sorted alphabetically.
   */
  function autoDetectCategories(products) {
    var seen = {};
    var cats = [];
    products.forEach(function (p) {
      var cat = getKitCategory(p);
      if (cat && !seen[cat]) {
        seen[cat] = true;
        cats.push({ tagSuffix: cat, label: capitalise(cat) });
      }
    });
    return cats.sort(function (a, b) { return a.label.localeCompare(b.label); });
  }

  /**
   * Returns the default selected category: first block category,
   * then legacy defaultCategory setting, then 'all'.
   */
  function getDefaultCategory() {
    if (window.KitBuilder.categories && window.KitBuilder.categories.length > 0) {
      return window.KitBuilder.categories[0].tagSuffix;
    }
    return window.KitBuilder.settings.defaultCategory || 'all';
  }

  function capitalise(str) {
    if (!str) return '';
    return str.charAt(0).toUpperCase() + str.slice(1);
  }

  // -- RENDER CATEGORIES -----------------------------------------------------

  function renderCategories(products) {
    var tabs = document.getElementById('kb-category-tabs');
    if (!tabs) return;

    // Merchant-defined order via blocks takes precedence; fall back to tag auto-detection
    var cats = (window.KitBuilder.categories && window.KitBuilder.categories.length > 0)
      ? window.KitBuilder.categories
      : autoDetectCategories(products);

    if (cats.length <= 1) {
      tabs.innerHTML = '';
      return;
    }

    tabs.innerHTML = cats.map(function (cat) {
      var active = cat.tagSuffix === state.selectedCategory;
      return (
        '<button class="kit-builder__tab' + (active ? ' is-active' : '') + '" ' +
          'data-type="' + esc(cat.tagSuffix) + '" ' +
          'role="tab" aria-selected="' + (active ? 'true' : 'false') + '">' +
          esc(cat.label) +
        '</button>'
      );
    }).join('');

    tabs.querySelectorAll('.kit-builder__tab').forEach(function (tab) {
      tab.addEventListener('click', function () {
        state.selectedCategory = this.dataset.type;
        tabs.querySelectorAll('.kit-builder__tab').forEach(function (t) {
          t.classList.remove('is-active');
          t.setAttribute('aria-selected', 'false');
        });
        this.classList.add('is-active');
        this.setAttribute('aria-selected', 'true');
        renderProducts();
      });
    });
  }

  // -- RENDER PRODUCTS -------------------------------------------------------

  /**
   * Full product grid rebuild.
   * skipCategories: pass true when re-rendering after a variant swap -- tabs don't change.
   */
  function renderProducts(skipCategories) {
    var allProducts = getProductsForColour(state.selectedColour);
    if (!skipCategories) renderCategories(allProducts);

    // Filter by tag-based category
    var filtered = state.selectedCategory === 'all'
      ? allProducts
      : allProducts.filter(function (p) { return getKitCategory(p) === state.selectedCategory; });

    var chip = document.getElementById('kb-colour-label');
    if (chip) {
      var fam = getFamilyByKey(state.selectedColour);
      var chipLabel = fam ? fam.label : state.selectedColour;
      // Use the full metallic gradient (same as the banner) so the pill looks
      // as rich as the colour swatch dot.  Fall back to flat hex if no gradient.
      var chipBg = fam
        ? (fam.bannerGradient || fam.bannerHex)
        : colourNameToCSS(state.selectedColour);
      chip.textContent = chipLabel;
      chip.style.background = chipBg;
      // All known colour families are either very dark (black) or bright metallic
      // -- white text looks correct on all of them.  Only fall back to the dark
      // check for unknown/custom colours where we have no family entry.
      chip.style.color = fam ? '#fff' : (isColourDark(fam ? fam.bannerHex : chipBg) ? '#fff' : '#111');
    }

    var grid = document.getElementById('kb-product-grid');
    if (!grid) return;

    if (filtered.length === 0) {
      grid.innerHTML = '<p class="kit-builder__empty">No products available in this colour and category.</p>';
      return;
    }

    // Determine card type per product:
    //  kit-show-variants        -> one standard card PER VARIANT (each looks like its own product)
    //  kit-colour-agnostic only -> standard qty-stepper card (single accessories)
    //  colour product, 2+ family variants -> variant-pill picker (colour option selection)
    //  colour product, 1 family variant   -> standard qty-stepper card
    //
    // kit-show-variants is expanded into individual cards so customers never see
    // a pill picker -- each variant of the product becomes an independent selectable card.
    grid.innerHTML = filtered.map(function (product) {
      if (hasTag(product, 'kit-show-variants')) {
        // Matrix products (Height x Pieces): one card per unique Height value.
        // Avoids showing 4 cards for a 2-height x 2-pieces product when the customer
        // only needs to choose by height.
        if (isMatrixProduct(product)) return renderMatrixGroupedCards(product);
        // Other show-variants products: one standard card per variant.
        return product.variants.map(function (v) {
          return renderVariantAsStandardCard(product, v);
        }).join('');
      }
      if (hasTag(product, 'kit-colour-agnostic')) {
        return renderStandardCard(product, getDefaultVariant(product));
      }
      var fvs = getVariantsForFamily(product, state.selectedColour);
      if (fvs.length > 1) return renderShowVariantsCard(product, fvs);
      // fvs[0] = exact colour match; fallback = best affinity match (e.g. 'gold top'
      // variant shown in the gold kit); final fallback = first available variant.
      var displayVariant = fvs[0] ||
        getBestAffinityVariant(product, state.selectedColour) ||
        getDefaultVariant(product);
      return renderStandardCard(product, displayVariant);
    }).join('');

    // Standard card -- click card or +/- buttons
    // Exclude matrix-height cards (interaction is via pieces-row steppers, not card-level click)
    grid.querySelectorAll('.kit-builder__product-card:not(.kit-builder__product-card--variants):not(.kit-builder__matrix-height-card)').forEach(function (card) {
      card.addEventListener('click', function (e) {
        if (e.target.closest('.kit-builder__product-qty')) return;
        if (this.dataset.available === '0') return;
        var variantId = parseInt(this.dataset.variantId, 10);
        var productId = parseInt(this.dataset.productId, 10);
        var alreadyInKit = state.kitItems.some(function (i) { return i.variant.id === variantId; });
        // Toggle: click to add if not in kit; click again to deselect entirely.
        // (Use +/- buttons to adjust qty without toggling off.)
        if (alreadyInKit) {
          removeAll(variantId);
        } else {
          addItem(productId, variantId);
        }
      });

      card.addEventListener('keydown', function (e) {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          if (this.dataset.available !== '0') this.click();
        }
      });
    });

    grid.querySelectorAll('.kit-builder__qty-btn').forEach(function (btn) {
      btn.addEventListener('click', function (e) {
        e.stopPropagation();
        var variantId = parseInt(this.dataset.variantId, 10);
        var productId = parseInt(this.dataset.productId, 10);
        if (this.dataset.action === 'increase') {
          addItem(productId, variantId);
        } else {
          removeItem(variantId);
        }
      });
    });

    // Variant-picker card -- clicking a pill toggles that variant in/out of kit
    grid.querySelectorAll('.kit-builder__variant-btn').forEach(function (btn) {
      btn.addEventListener('click', function (e) {
        e.stopPropagation();
        var variantId = parseInt(this.dataset.variantId, 10);
        var productId = parseInt(this.dataset.productId, 10);
        toggleVariant(productId, variantId);
      });
    });

    // Matrix chip remove -- removes an added combo entirely
    grid.querySelectorAll('.kit-builder__chip-remove').forEach(function (btn) {
      btn.addEventListener('click', function (e) {
        e.stopPropagation();
        var variantId = parseInt(this.dataset.variantId, 10);
        removeAll(variantId);
      });
    });

    // Matrix card -- clicking a size or quantity pill
    grid.querySelectorAll('.kit-builder__matrix-btn').forEach(function (btn) {
      btn.addEventListener('click', function (e) {
        e.stopPropagation();
        var productId = parseInt(this.dataset.productId, 10);
        selectMatrixOption(productId, this.dataset.optionType, this.dataset.optionValue);
      });
    });

    // Re-measure picker-header height after tabs are rendered (tab count can
    // vary per colour, changing the header height on mobile).
    updatePickerHeight();
  }

  /**
   * Renders a standard qty-stepper product card.
   * `variant` is passed in by the caller (resolved upstream via getVariantsForFamily
   * or getDefaultVariant), so this function is colour-system agnostic.
   */
  function renderStandardCard(product, variant) {
    var isAvailable = variant.available;
    var kitItem = state.kitItems.find(function (i) { return i.variant.id === variant.id; });
    var qty = kitItem ? kitItem.qty : 0;
    var img = variant.image || product.image;
    var selected = qty > 0;

    return (
      '<div class="kit-builder__product-card' +
          (selected ? ' is-selected' : '') +
          (isAvailable ? '' : ' is-oos') + '" ' +
        'data-variant-id="' + variant.id + '" ' +
        'data-product-id="' + product.id + '" ' +
        'data-available="' + (isAvailable ? '1' : '0') + '" ' +
        'role="listitem" ' +
        'tabindex="' + (isAvailable ? '0' : '-1') + '" ' +
        'aria-label="' + esc(product.title) +
          (isAvailable
            ? (selected ? ' -- in kit' : ' -- click to add')
            : ' -- out of stock') +
          '">' +
        '<div class="kit-builder__product-img-wrap">' +
          '<img ' +
            'class="kit-builder__product-img" ' +
            'src="' + esc(img) + '" ' +
            'alt="' + esc(product.image_alt) + '" ' +
            'loading="lazy" ' +
            'width="300" height="300">' +
          (!isAvailable
            ? '<div class="kit-builder__oos-overlay" aria-hidden="true">Out of Stock</div>'
            : '') +
          (qty > 0
            ? '<span class="kit-builder__product-badge" aria-label="' + qty + ' in kit">' + qty + '</span>'
            : '') +
        '</div>' +
        '<div class="kit-builder__product-info">' +
          '<p class="kit-builder__product-title">' + esc(product.title) + '</p>' +
          '<p class="kit-builder__product-price">' + esc(variant.price_formatted) + '</p>' +
          (!isAvailable ? '<p class="kit-builder__oos-label">Out of Stock</p>' : '') +
        '</div>' +
        '<div class="kit-builder__product-qty">' +
          '<button ' +
            'class="kit-builder__qty-btn" ' +
            'data-action="decrease" ' +
            'data-variant-id="' + variant.id + '" ' +
            'aria-label="Remove one ' + esc(product.title) + ' from kit"' +
            (qty === 0 ? ' disabled' : '') + '>-</button>' +
          '<span class="kit-builder__qty-val" aria-live="polite">' + qty + '</span>' +
          '<button ' +
            'class="kit-builder__qty-btn" ' +
            'data-action="increase" ' +
            'data-variant-id="' + variant.id + '" ' +
            'data-product-id="' + product.id + '" ' +
            'aria-label="Add one ' + esc(product.title) + ' to kit"' +
            (!isAvailable ? ' disabled' : '') + '>+</button>' +
        '</div>' +
      '</div>'
    );
  }

  /**
   * Renders a variant-picker card showing pill buttons for each variant.
   * Used for:
   *   - kit-show-variants products (all variants, e.g. quantity packs)
   *   - colour products with multiple variants in the chosen family
   *     (e.g. Tweezers showing Full Gold + Gold Top pills when Gold is selected)
   *
   * `variantsToShow` is the pre-filtered array of variants to render as pills.
   * Clicking a pill selects that variant; clicking the selected pill deselects.
   */
  /**
   * Renders a variant-picker card (Option A multi-variant version).
   *
   * Each pill is independently toggleable -- clicking adds the variant if absent,
   * removes it if already in kit. Multiple variants of the same product can coexist.
   *
   * The shared qty stepper operates on the "active" variant for this product, tracked
   * in state.activePillVariantPerProduct. Clicking any pill makes it the active one.
   * The badge on the image shows the total qty across ALL selected variants.
   */

  /**
   * For matrix (Height x Pieces) products tagged kit-show-variants:
   * renders one standard card per unique Height value, using the first available
   * variant at that height. The card subtitle shows just the height ("5cm"),
   * keeping the UI clean when customers only need to distinguish by height.
   */
  function renderMatrixGroupedCards(product) {
    var heightIdx = -1;
    product.options.forEach(function (opt, i) {
      if ((opt.name || '').toLowerCase().trim() === 'height') heightIdx = i;
    });
    if (heightIdx < 0) {
      // No Height option found -- fall back to one card per variant
      return product.variants.map(function (v) {
        return renderVariantAsStandardCard(product, v);
      }).join('');
    }

    // Collect unique height values in variant order
    var seenHeights = {}, heights = [];
    product.variants.forEach(function (v) {
      var h = v['option' + (heightIdx + 1)];
      if (h && !seenHeights[h]) { seenHeights[h] = true; heights.push(h); }
    });

    // One card per height; all piece-count variants for that height shown inside
    return heights.map(function (h) {
      var variantsAtHeight = product.variants.filter(function (v) {
        return v['option' + (heightIdx + 1)] === h;
      });
      if (!variantsAtHeight.length) return '';
      return renderMatrixHeightCard(product, h, variantsAtHeight, heightIdx);
    }).join('');
  }

  /**
   * Renders a single height-grouped card for a matrix product.
   * The card title shows the product name + height subtitle; inside the card body
   * each "Pieces" variant gets its own labelled +/- stepper row so customers can
   * independently add 6-piece and 12-piece packs of the same height to their kit.
   * No card-level click toggle -- interaction is entirely through the stepper rows.
   */
  function renderMatrixHeightCard(product, heightVal, variants, heightIdx) {
    // Find Pieces option index (the other dimension)
    var piecesIdx = -1;
    product.options.forEach(function (opt, i) {
      if (i !== heightIdx) piecesIdx = i; // use the first non-height option
    });

    // Total qty of all piece-count variants at this height (for badge)
    var totalQty = 0;
    variants.forEach(function (v) {
      var item = state.kitItems.find(function (i) { return i.variant.id === v.id; });
      if (item) totalQty += item.qty;
    });

    var isSelected = totalQty > 0;
    var img = variants[0].image || product.image;

    // Build one row per pieces variant
    var rows = variants.map(function (v) {
      var piecesLabel = (piecesIdx >= 0)
        ? v['option' + (piecesIdx + 1)]
        : (v.title && v.title !== 'Default Title' ? v.title : '');
      var kitItem = state.kitItems.find(function (i) { return i.variant.id === v.id; });
      var qty     = kitItem ? kitItem.qty : 0;
      var avail   = v.available;
      return (
        '<div class="kit-builder__pieces-row' + (!avail ? ' is-oos' : '') + '">' +
          '<span class="kit-builder__pieces-label">' + esc(piecesLabel) + '</span>' +
          (avail
            ? '<span class="kit-builder__pieces-price">' + esc(v.price_formatted) + '</span>'
            : '<span class="kit-builder__pieces-price kit-builder__pieces-oos-text">Sold Out</span>') +
          '<div class="kit-builder__product-qty">' +
            '<button class="kit-builder__qty-btn" data-action="decrease" ' +
              'data-variant-id="' + v.id + '" ' +
              'aria-label="Remove ' + esc(piecesLabel) + ' ' + esc(product.title) + ' from kit"' +
              (qty === 0 ? ' disabled' : '') + '>-</button>' +
            '<span class="kit-builder__qty-val" aria-live="polite">' + qty + '</span>' +
            '<button class="kit-builder__qty-btn" data-action="increase" ' +
              'data-variant-id="' + v.id + '" ' +
              'data-product-id="' + product.id + '" ' +
              'aria-label="Add ' + esc(piecesLabel) + ' ' + esc(product.title) + ' to kit"' +
              (!avail ? ' disabled' : '') + '>+</button>' +
          '</div>' +
        '</div>'
      );
    }).join('');

    return (
      '<div class="kit-builder__product-card kit-builder__matrix-height-card' +
          (isSelected ? ' is-selected' : '') + '" ' +
        'data-product-id="' + product.id + '" ' +
        'role="listitem">' +
        '<div class="kit-builder__product-img-wrap">' +
          '<img class="kit-builder__product-img" src="' + esc(img) + '" ' +
            'alt="' + esc(product.image_alt) + '" loading="lazy" width="300" height="300">' +
          (totalQty > 0
            ? '<span class="kit-builder__product-badge" aria-label="' + totalQty + ' in kit">' + totalQty + '</span>'
            : '') +
        '</div>' +
        '<div class="kit-builder__product-info">' +
          '<p class="kit-builder__product-title">' + esc(product.title) +
            '<span class="kit-builder__variant-subtitle">' + esc(heightVal) + '</span>' +
          '</p>' +
          '<div class="kit-builder__pieces-options">' + rows + '</div>' +
        '</div>' +
      '</div>'
    );
  }

  /**
   * Renders a single variant of a kit-show-variants product as a standard qty-stepper card.
   * Used to "expand" multi-variant products into individual cards -- each variant looks like
   * its own product to the customer. The variant title is shown as a subtitle beneath the
   * product title so the customer knows which size/option they are adding.
   * Uses the same click-to-add / +/- stepper behaviour as renderStandardCard.
   *
   * Optional `subtitleOverride`: if provided, shown instead of variant.title as the subtitle.
   * Used by renderMatrixGroupedCards to show just the height value ("5cm") rather than
   * the full variant title ("5cm / 6 Pieces").
   */
  function renderVariantAsStandardCard(product, variant, subtitleOverride) {
    var isAvailable  = variant.available;
    var kitItem      = state.kitItems.find(function (i) { return i.variant.id === variant.id; });
    var qty          = kitItem ? kitItem.qty : 0;
    var img          = variant.image || product.image;
    var selected     = qty > 0;
    var varSubtitle  = subtitleOverride !== undefined
      ? subtitleOverride
      : ((variant.title && variant.title !== 'Default Title') ? variant.title : '');

    return (
      '<div class="kit-builder__product-card' +
          (selected ? ' is-selected' : '') +
          (isAvailable ? '' : ' is-oos') + '" ' +
        'data-variant-id="' + variant.id + '" ' +
        'data-product-id="' + product.id + '" ' +
        'data-available="' + (isAvailable ? '1' : '0') + '" ' +
        'role="listitem" ' +
        'tabindex="' + (isAvailable ? '0' : '-1') + '" ' +
        'aria-label="' + esc(product.title) + (varSubtitle ? ' ' + varSubtitle : '') +
          (isAvailable ? (selected ? ' -- in kit' : ' -- click to add') : ' -- out of stock') + '">' +
        '<div class="kit-builder__product-img-wrap">' +
          '<img ' +
            'class="kit-builder__product-img" ' +
            'src="' + esc(img) + '" ' +
            'alt="' + esc(product.image_alt) + '" ' +
            'loading="lazy" ' +
            'width="300" height="300">' +
          (!isAvailable ? '<div class="kit-builder__oos-overlay" aria-hidden="true">Out of Stock</div>' : '') +
          (qty > 0 ? '<span class="kit-builder__product-badge" aria-label="' + qty + ' in kit">' + qty + '</span>' : '') +
        '</div>' +
        '<div class="kit-builder__product-info">' +
          '<p class="kit-builder__product-title">' + esc(product.title) +
            (varSubtitle ? '<span class="kit-builder__variant-subtitle">' + esc(varSubtitle) + '</span>' : '') +
          '</p>' +
          '<p class="kit-builder__product-price">' + esc(variant.price_formatted) + '</p>' +
          (!isAvailable ? '<p class="kit-builder__oos-label">Out of Stock</p>' : '') +
        '</div>' +
        '<div class="kit-builder__product-qty">' +
          '<button ' +
            'class="kit-builder__qty-btn" ' +
            'data-action="decrease" ' +
            'data-variant-id="' + variant.id + '" ' +
            'aria-label="Remove one ' + esc(product.title) + ' from kit"' +
            (qty === 0 ? ' disabled' : '') + '>-</button>' +
          '<span class="kit-builder__qty-val" aria-live="polite">' + qty + '</span>' +
          '<button ' +
            'class="kit-builder__qty-btn" ' +
            'data-action="increase" ' +
            'data-variant-id="' + variant.id + '" ' +
            'data-product-id="' + product.id + '" ' +
            'aria-label="Add one ' + esc(product.title) + ' to kit"' +
            (!isAvailable ? ' disabled' : '') + '>+</button>' +
        '</div>' +
      '</div>'
    );
  }

  function renderShowVariantsCard(product, variantsToShow) {
    // All kit items currently selected for this product (non-addon)
    var productKitItems = state.kitItems.filter(function (i) {
      return i.product.id === product.id && !i.isAddon;
    });
    var isSelected = productKitItems.length > 0;
    var totalQty   = productKitItems.reduce(function (sum, i) { return sum + i.qty; }, 0);

    // Resolve the active variant for the shared qty stepper
    var activeVid = state.activePillVariantPerProduct[product.id] || null;
    // Guard: active variant might have been removed
    if (activeVid && !state.kitItems.find(function (i) { return i.variant.id === activeVid; })) {
      activeVid = null;
      state.activePillVariantPerProduct[product.id] = null;
    }
    var activeKitItem = activeVid
      ? state.kitItems.find(function (i) { return i.variant.id === activeVid; })
      : null;
    // Fall back to first in-kit item if no explicit active
    if (!activeKitItem && productKitItems.length > 0) {
      activeKitItem = productKitItems[0];
      activeVid     = activeKitItem.variant.id;
      state.activePillVariantPerProduct[product.id] = activeVid;
    }

    var qty          = activeKitItem ? activeKitItem.qty : 0;
    var hasSelection = !!activeKitItem;

    // Display image and price from active (or first available) variant
    var displayVariant = activeKitItem
      ? activeKitItem.variant
      : (variantsToShow.find(function (v) { return v.available; }) || variantsToShow[0]);
    var img            = displayVariant.image || product.image;
    var priceFormatted = activeKitItem
      ? activeKitItem.variant.price_formatted
      : displayVariant.price_formatted;

    // Variant pills -- each independently toggleable
    var variantBtns = variantsToShow.map(function (v) {
      var kitItem = state.kitItems.find(function (i) { return i.variant.id === v.id; });
      var isInKit = !!kitItem;
      var pillQty = kitItem ? kitItem.qty : 0;
      return (
        '<button class="kit-builder__variant-btn' +
            (isInKit ? ' is-selected' : '') +
            (!v.available ? ' is-oos' : '') + '" ' +
          'data-variant-id="' + v.id + '" ' +
          'data-product-id="' + product.id + '" ' +
          'role="checkbox" ' +
          'aria-checked="' + (isInKit ? 'true' : 'false') + '" ' +
          'aria-label="' + esc(v.title) + (v.available ? '' : ' -- out of stock') + '"' +
          (!v.available ? ' disabled' : '') + '>' +
          esc(v.title) +
          (pillQty > 0 ? '<span class="kb-pill-qty">' + pillQty + '</span>' : '') +
        '</button>'
      );
    }).join('');

    // Qty stepper -- operates on the active variant (the last pill clicked)
    var qtyVid = activeVid || 0;
    var qtyRow =
      '<div class="kit-builder__product-qty">' +
        '<button class="kit-builder__qty-btn" ' +
          'data-action="decrease" ' +
          'data-variant-id="' + qtyVid + '" ' +
          'data-product-id="' + product.id + '" ' +
          'aria-label="Remove one ' + esc(product.title) + ' from kit"' +
          (!hasSelection || qty === 0 ? ' disabled' : '') + '>-</button>' +
        '<span class="kit-builder__qty-val" aria-live="polite">' + qty + '</span>' +
        '<button class="kit-builder__qty-btn" ' +
          'data-action="increase" ' +
          'data-variant-id="' + qtyVid + '" ' +
          'data-product-id="' + product.id + '" ' +
          'aria-label="Add one ' + esc(product.title) + ' to kit"' +
          (!hasSelection ? ' disabled' : '') + '>+</button>' +
      '</div>';

    return (
      '<div class="kit-builder__product-card kit-builder__product-card--variants' +
          (isSelected ? ' is-selected' : '') + '" ' +
        'data-product-id="' + product.id + '" ' +
        'role="listitem">' +
        '<div class="kit-builder__product-img-wrap">' +
          '<img ' +
            'class="kit-builder__product-img" ' +
            'src="' + esc(img) + '" ' +
            'alt="' + esc(product.image_alt) + '" ' +
            'loading="lazy" ' +
            'width="300" height="300">' +
          (isSelected
            ? '<span class="kit-builder__product-badge" aria-label="' + totalQty + ' in kit">' + totalQty + '</span>'
            : '') +
        '</div>' +
        '<div class="kit-builder__product-info">' +
          '<p class="kit-builder__product-title">' + esc(product.title) + '</p>' +
          '<p class="kit-builder__product-price">' + esc(priceFormatted) + '</p>' +
        '</div>' +
        '<div class="kit-builder__variant-btns" role="group" aria-label="' +
          esc('Select ' + product.title + ' variants') + '">' +
          variantBtns +
        '</div>' +
        qtyRow +
      '</div>'
    );
  }

  /**
   * (Formerly swapVariant comment block -- now handled by toggleVariant above.)
   */

  // -- MATRIX CARD (Size x Quantity two-option products) ----------------------

  /**
   * Returns true if a product has exactly a "Height" option and a "Pieces" option.
   * Used to route kit-show-variants products to the two-row matrix card renderer.
   */
  function isMatrixProduct(product) {
    if (!product.options || product.options.length < 2) return false;
    var names = product.options.map(function (o) {
      return (o.name || '').toLowerCase().trim();
    });
    return names.indexOf('height') >= 0 && names.indexOf('pieces') >= 0;
  }

  /**
   * Finds the variant that matches a given height value + pieces value.
   * Returns null if no matching variant exists.
   */
  function resolveMatrixVariant(product, heightVal, piecesVal) {
    var heightIdx = -1, piecesIdx = -1;
    product.options.forEach(function (opt, i) {
      var n = (opt.name || '').toLowerCase().trim();
      if (n === 'height') heightIdx = i;
      if (n === 'pieces') piecesIdx = i;
    });
    if (heightIdx < 0 || piecesIdx < 0) return null;
    return product.variants.find(function (v) {
      return v['option' + (heightIdx + 1)] === heightVal &&
             v['option' + (piecesIdx + 1)] === piecesVal;
    }) || null;
  }

  /**
   * Handles a pill click on a matrix card (Option A multi-variant version).
   *
   * Tracks the in-progress combination in state.pendingVariantSelections.
   * Once both Height and Pieces are chosen:
   *   - If that combination is already in the kit: removes it (toggle off).
   *   - Otherwise: adds it and resets the pending selection so another combo can be picked.
   * Previously added combinations for the same product are NOT disturbed when the
   * pending selection changes -- they remain in the kit independently.
   */
  function selectMatrixOption(productId, optionType, optionValue) {
    if (!state.pendingVariantSelections[productId]) {
      state.pendingVariantSelections[productId] = { heightVal: null, piecesVal: null };
    }
    var pending = state.pendingVariantSelections[productId];

    // Toggle the clicked option off if it was already the pending selection
    if (optionType === 'height') {
      pending.heightVal = (pending.heightVal === optionValue) ? null : optionValue;
    } else {
      pending.piecesVal = (pending.piecesVal === optionValue) ? null : optionValue;
    }

    // If both options are now selected, resolve and commit
    if (pending.heightVal && pending.piecesVal) {
      var product = window.KitBuilder.products.find(function (p) { return p.id === productId; });
      if (product) {
        var variant = resolveMatrixVariant(product, pending.heightVal, pending.piecesVal);
        if (variant && variant.available) {
          var alreadyIn = state.kitItems.find(function (i) { return i.variant.id === variant.id; });
          if (alreadyIn) {
            // Toggle off: remove this combo from the kit
            state.kitItems = state.kitItems.filter(function (i) { return i.variant.id !== variant.id; });
          } else {
            // Add the new combo -- other combos for this product stay untouched
            state.kitItems.push({
              product:        product,
              variant:        variant,
              qty:            1,
              priceFormatted: variant.price_formatted,
              priceCents:     variant.price_cents
            });
          }
        }
        // Reset pending so the user can pick another combination
        pending.heightVal = null;
        pending.piecesVal = null;
      }
    }

    renderProducts(true);
    renderMosaic();
    updateNextButton();
    updateImageColState();
  }

  /**
   * Renders a two-row matrix card for kit-show-variants products with Size + Quantity options.
   * Row 1: Size pills.  Row 2: Quantity pills.
   * Qty stepper appears below; buttons are disabled until both options are selected.
   */
  /**
   * Renders a two-row matrix card (Option A multi-variant version).
   *
   * In-kit combos are shown as removable chips above the pickers.
   * After selecting a combo the pickers reset, letting the user pick a second one.
   * The badge on the image shows the total qty across all selected combos.
   */
  function renderMatrixCard(product, variantsToShow) {
    var pending = state.pendingVariantSelections[product.id] || { heightVal: null, piecesVal: null };

    // Find option indices
    var heightIdx = -1, piecesIdx = -1;
    product.options.forEach(function (opt, i) {
      var n = (opt.name || '').toLowerCase().trim();
      if (n === 'height') heightIdx = i;
      if (n === 'pieces') piecesIdx = i;
    });

    // Collect unique height and pieces values (preserving variant order)
    var seenHeights = {}, seenPieces = {}, heights = [], pieces = [];
    variantsToShow.forEach(function (v) {
      var h = v['option' + (heightIdx + 1)];
      var p = v['option' + (piecesIdx + 1)];
      if (h && !seenHeights[h]) { seenHeights[h] = true; heights.push(h); }
      if (p && !seenPieces[p])  { seenPieces[p]  = true; pieces.push(p); }
    });

    // All in-kit combos for this product (Option A: there can be multiple)
    var productKitItems = state.kitItems.filter(function (i) {
      return i.product.id === product.id && !i.isAddon;
    });
    var totalQty   = productKitItems.reduce(function (sum, i) { return sum + i.qty; }, 0);
    var isSelected = productKitItems.length > 0;

    // Resolved variant (if both pending options chosen)
    var resolvedVariant = (pending.heightVal && pending.piecesVal)
      ? resolveMatrixVariant(product, pending.heightVal, pending.piecesVal)
      : null;

    // Display image and price: prefer first in-kit item, then resolved pending, then first available
    var displayVariant = (productKitItems[0] && productKitItems[0].variant) ||
      resolvedVariant ||
      variantsToShow.find(function (v) { return v.available; }) ||
      variantsToShow[0];
    var img = displayVariant.image || product.image;
    var priceFormatted = (productKitItems[0] && productKitItems[0].variant.price_formatted) ||
      (resolvedVariant ? resolvedVariant.price_formatted : displayVariant.price_formatted);

    // Height pills -- OOS if no variant with this height is available
    var heightBtns = heights.map(function (h) {
      var isSel = pending.heightVal === h;
      var hasAvail = variantsToShow.some(function (v) {
        return v['option' + (heightIdx + 1)] === h && v.available;
      });
      return (
        '<button class="kit-builder__matrix-btn' +
            (isSel ? ' is-selected' : '') +
            (!hasAvail ? ' is-oos' : '') + '" ' +
          'data-product-id="' + product.id + '" ' +
          'data-option-type="height" ' +
          'data-option-value="' + esc(h) + '"' +
          (!hasAvail ? ' disabled' : '') + '>' +
          esc(h) +
        '</button>'
      );
    }).join('');

    // Pieces pills -- OOS if unavailable for the currently selected height (or in general)
    var piecesBtns = pieces.map(function (p) {
      var isSel = pending.piecesVal === p;
      var hasAvail = variantsToShow.some(function (v) {
        var heightMatch = !pending.heightVal || v['option' + (heightIdx + 1)] === pending.heightVal;
        return v['option' + (piecesIdx + 1)] === p && heightMatch && v.available;
      });
      return (
        '<button class="kit-builder__matrix-btn' +
            (isSel ? ' is-selected' : '') +
            (!hasAvail ? ' is-oos' : '') + '" ' +
          'data-product-id="' + product.id + '" ' +
          'data-option-type="pieces" ' +
          'data-option-value="' + esc(p) + '"' +
          (!hasAvail ? ' disabled' : '') + '>' +
          esc(p) +
        '</button>'
      );
    }).join('');

    // Chips for already-added combos (shown above the pickers)
    var chipsHtml = '';
    if (productKitItems.length > 0) {
      chipsHtml =
        '<div class="kit-builder__matrix-chips" aria-label="Added sizes">' +
          productKitItems.map(function (ki) {
            var varTitle = ki.variant.title !== 'Default Title' ? ki.variant.title : esc(product.title);
            return (
              '<span class="kit-builder__matrix-chip">' +
                esc(varTitle) +
                (ki.qty > 1 ? ' &times;' + ki.qty : '') +
                '<button ' +
                  'class="kit-builder__chip-remove" ' +
                  'data-variant-id="' + ki.variant.id + '" ' +
                  'aria-label="Remove ' + esc(varTitle) + ' from kit" ' +
                  'type="button">' +
                  '&times;' +
                '</button>' +
              '</span>'
            );
          }).join('') +
        '</div>';
    }

    // Qty stepper -- tied to the resolved pending variant (if both options chosen)
    var hasFullSelection = !!(pending.heightVal && pending.piecesVal);
    // Find qty of the currently-resolved pending variant (if it's in kit)
    var pendingKitItem = resolvedVariant
      ? state.kitItems.find(function (i) { return i.variant.id === resolvedVariant.id; })
      : null;
    var pendingQty = pendingKitItem ? pendingKitItem.qty : 0;
    var qtyVid = resolvedVariant ? resolvedVariant.id : 0;
    var qtyRow =
      '<div class="kit-builder__product-qty">' +
        '<button class="kit-builder__qty-btn" ' +
          'data-action="decrease" ' +
          'data-variant-id="' + qtyVid + '" ' +
          'data-product-id="' + product.id + '" ' +
          'aria-label="Remove one ' + esc(product.title) + ' from kit"' +
          (!hasFullSelection || pendingQty === 0 ? ' disabled' : '') + '>-</button>' +
        '<span class="kit-builder__qty-val" aria-live="polite">' + pendingQty + '</span>' +
        '<button class="kit-builder__qty-btn" ' +
          'data-action="increase" ' +
          'data-variant-id="' + qtyVid + '" ' +
          'data-product-id="' + product.id + '" ' +
          'aria-label="Add one ' + esc(product.title) + ' to kit"' +
          (!hasFullSelection ? ' disabled' : '') + '>+</button>' +
      '</div>';

    return (
      '<div class="kit-builder__product-card kit-builder__product-card--matrix' +
          (isSelected ? ' is-selected' : '') + '" ' +
        'data-product-id="' + product.id + '" ' +
        'role="listitem">' +
        '<div class="kit-builder__product-img-wrap">' +
          '<img ' +
            'class="kit-builder__product-img" ' +
            'src="' + esc(img) + '" ' +
            'alt="' + esc(product.image_alt) + '" ' +
            'loading="lazy" ' +
            'width="300" height="300">' +
          (isSelected
            ? '<span class="kit-builder__product-badge" aria-label="' + totalQty + ' in kit">' + totalQty + '</span>'
            : '') +
        '</div>' +
        '<div class="kit-builder__product-info">' +
          '<p class="kit-builder__product-title">' + esc(product.title) + '</p>' +
          '<p class="kit-builder__product-price">' + esc(priceFormatted) + '</p>' +
        '</div>' +
        chipsHtml +
        '<div class="kit-builder__matrix-row">' +
          '<span class="kit-builder__matrix-label">Height</span>' +
          '<div class="kit-builder__variant-btns" role="radiogroup" ' +
            'aria-label="' + esc(product.title) + ' height">' +
            heightBtns +
          '</div>' +
        '</div>' +
        '<div class="kit-builder__matrix-row">' +
          '<span class="kit-builder__matrix-label">Pieces</span>' +
          '<div class="kit-builder__variant-btns" role="radiogroup" ' +
            'aria-label="' + esc(product.title) + ' pieces">' +
            piecesBtns +
          '</div>' +
        '</div>' +
        qtyRow +
      '</div>'
    );
  }

  /**
   * Toggles a variant pill on a show-variants card.
   * Option A multi-variant: each variant is independent -- clicking adds if absent,
   * removes entirely if already in kit. Other variants of the same product are untouched.
   * Updates activePillVariantPerProduct so the shared qty stepper targets the last-touched variant.
   */
  function toggleVariant(productId, variantId) {
    var existingItem = state.kitItems.find(function (i) { return i.variant.id === variantId; });

    if (existingItem) {
      // Remove this variant entirely (toggle off)
      state.kitItems = state.kitItems.filter(function (i) { return i.variant.id !== variantId; });
      // Update active: if we removed the active variant, promote any remaining sibling
      if (state.activePillVariantPerProduct[productId] === variantId) {
        var sibling = state.kitItems.find(function (i) {
          return i.product.id === productId && !i.isAddon;
        });
        state.activePillVariantPerProduct[productId] = sibling ? sibling.variant.id : null;
      }
    } else {
      var product = window.KitBuilder.products.find(function (p) { return p.id === productId; });
      if (!product) return;
      var variant = product.variants.find(function (v) { return v.id === variantId; });
      if (!variant || !variant.available) return;

      state.kitItems.push({
        product: product,
        variant: variant,
        qty: 1,
        priceFormatted: variant.price_formatted,
        priceCents: variant.price_cents
      });
      // This variant becomes the active one for the shared stepper
      state.activePillVariantPerProduct[productId] = variantId;
    }

    renderProducts(true);
    renderMosaic();
    updateNextButton();
    updateImageColState();
  }

  /**
   * Fast in-place update of a single product card's qty/badge/selection state.
   * Called instead of a full renderProducts() on add/remove -- no innerHTML rebuild,
   * no event rebinding, no category re-render. Falls back to renderProducts(true)
   * if the card isn't currently in the DOM (filtered by category or variant card).
   */
  function updateProductCardInPlace(variantId) {
    var grid = document.getElementById('kb-product-grid');
    if (!grid) { renderProducts(true); return; }

    var el = grid.querySelector('[data-variant-id="' + variantId + '"]');

    // For matrix-height cards, data-variant-id lives on the row buttons, not the card.
    // Walk up to find the height card and update all its rows in-place.
    if (el && !el.classList.contains('kit-builder__product-card')) {
      var heightCard = el.closest('.kit-builder__matrix-height-card');
      if (heightCard) {
        updateMatrixHeightCardInPlace(heightCard);
        return;
      }
      // For other non-standard cards (variant pills etc.), fall back to full re-render.
      renderProducts(true);
      return;
    }

    var card = el;
    if (!card) { renderProducts(true); return; }

    var kitItem = state.kitItems.find(function (i) { return i.variant.id === variantId; });
    var qty = kitItem ? kitItem.qty : 0;

    card.classList.toggle('is-selected', qty > 0);

    var qtyVal = card.querySelector('.kit-builder__qty-val');
    if (qtyVal) qtyVal.textContent = qty;

    var decBtn = card.querySelector('[data-action="decrease"]');
    if (decBtn) decBtn.disabled = qty === 0;

    var imgWrap = card.querySelector('.kit-builder__product-img-wrap');
    var badge   = card.querySelector('.kit-builder__product-badge');

    if (qty > 0) {
      if (badge) {
        badge.textContent = qty;
        badge.setAttribute('aria-label', qty + ' in kit');
      } else if (imgWrap) {
        var newBadge = document.createElement('span');
        newBadge.className = 'kit-builder__product-badge';
        newBadge.setAttribute('aria-label', qty + ' in kit');
        newBadge.textContent = qty;
        imgWrap.appendChild(newBadge);
      }
    } else if (badge) {
      badge.parentNode.removeChild(badge);
    }
  }

  /**
   * In-place update for a matrix-height card when a pieces-row stepper is clicked.
   * Updates every row's qty display + button state, the card's is-selected class,
   * and the aggregate badge on the card image.
   */
  function updateMatrixHeightCardInPlace(card) {
    var totalQty = 0;

    card.querySelectorAll('.kit-builder__pieces-row').forEach(function (row) {
      var incBtn = row.querySelector('[data-action="increase"]');
      var decBtn = row.querySelector('[data-action="decrease"]');
      var qtyVal = row.querySelector('.kit-builder__qty-val');
      if (!incBtn) return;

      var variantId = parseInt(incBtn.dataset.variantId, 10);
      var kitItem   = state.kitItems.find(function (i) { return i.variant.id === variantId; });
      var qty       = kitItem ? kitItem.qty : 0;
      totalQty += qty;

      if (qtyVal) qtyVal.textContent = qty;
      if (decBtn) decBtn.disabled = (qty === 0);
    });

    card.classList.toggle('is-selected', totalQty > 0);

    var imgWrap = card.querySelector('.kit-builder__product-img-wrap');
    var badge   = card.querySelector('.kit-builder__product-badge');

    if (totalQty > 0) {
      if (badge) {
        badge.textContent = totalQty;
        badge.setAttribute('aria-label', totalQty + ' in kit');
      } else if (imgWrap) {
        var nb = document.createElement('span');
        nb.className = 'kit-builder__product-badge';
        nb.setAttribute('aria-label', totalQty + ' in kit');
        nb.textContent = totalQty;
        imgWrap.appendChild(nb);
      }
    } else if (badge) {
      badge.parentNode.removeChild(badge);
    }
  }

  function addItem(productId, variantId) {
    var product = window.KitBuilder.products.find(function (p) { return p.id === productId; });
    if (!product) return;
    var variant = product.variants.find(function (v) { return v.id === variantId; });
    if (!variant) return;
    if (!variant.available) return; // belt-and-suspenders OOS guard

    var existing = state.kitItems.find(function (i) { return i.variant.id === variantId; });
    if (existing) {
      existing.qty++;
    } else {
      state.kitItems.push({
        product: product,
        variant: variant,
        qty: 1,
        priceFormatted: variant.price_formatted,
        priceCents: variant.price_cents
      });
    }

    updateProductCardInPlace(variantId);  // fast in-place DOM update, no full re-render
    renderMosaic();
    updateNextButton();
    updateImageColState();
  }

  function removeItem(variantId) {
    var idx = -1;
    state.kitItems.forEach(function (item, i) {
      if (item.variant.id === variantId) idx = i;
    });
    if (idx < 0) return;

    if (state.kitItems[idx].qty > 1) {
      state.kitItems[idx].qty--;
    } else {
      state.kitItems.splice(idx, 1);
    }

    updateProductCardInPlace(variantId);  // fast in-place DOM update, no full re-render
    renderMosaic();
    updateNextButton();
    updateImageColState();
  }

  /**
   * Removes a product from the kit entirely (all qty), used for click-to-deselect.
   * Differs from removeItem which decrements by 1.
   */
  function removeAll(variantId) {
    state.kitItems = state.kitItems.filter(function (i) { return i.variant.id !== variantId; });
    updateProductCardInPlace(variantId);
    renderMosaic();
    updateNextButton();
    updateImageColState();
  }

  /**
   * Update step-2 footer (always visible -- shows GBP0.00 before first item).
   * Total is always updated. Personalise button is disabled when kit is empty.
   */
  function updateNextButton() {
    var totalAmt    = document.getElementById('kb-total-amount');
    var totalAmtBot = document.getElementById('kb-total-amount-bottom');
    var totalAmtP3  = document.getElementById('kb-total-amount-p3');
    var btn         = document.getElementById('kb-personalise-btn');
    var btnBot      = document.getElementById('kb-personalise-btn-bottom');
    var hasItems    = state.kitItems.length > 0;
    var total       = formatTotal();

    var blocked = !hasItems || state.engraving.logoUploading;
    if (totalAmt)    totalAmt.textContent    = total;
    if (totalAmtBot) totalAmtBot.textContent = total;
    if (totalAmtP3)  totalAmtP3.textContent  = total;
    if (btn)         btn.disabled    = blocked;
    if (btnBot)      btnBot.disabled = blocked;
  }

  /**
   * Update image column visibility, mosaic fade, and mobile aspect ratio.
   * .has-colour on #kb-image-col: shows the col on mobile + activates sticky
   * .has-items on #kb-image-frame: triggers mosaic opacity transition
   * .kb-ratio--* on #kb-image-col: drives the CSS aspect-ratio step (mobile only)
   */
  function updateImageColState() {
    var hasColour = !!state.selectedColour;
    // Count only non-addon items for ratio/mosaic purposes
    var mainItems = state.kitItems.filter(function (i) { return !i.isAddon; });
    var hasItems  = mainItems.length > 0;
    var col   = document.getElementById('kb-image-col');
    var frame = document.getElementById('kb-image-frame');
    if (col)   col.classList.toggle('has-colour', hasColour);
    if (frame) frame.classList.toggle('has-items', hasItems);

    // Apply adaptive aspect-ratio class for mobile (CSS handles the visual change)
    if (col) {
      var allRatioClasses = ['kb-ratio--3x1', 'kb-ratio--3x2', 'kb-ratio--2x1', 'kb-ratio--4x3', 'kb-ratio--1x1'];
      var ratioClass = getMobileRatioClass(mainItems.length);
      allRatioClasses.forEach(function (cls) {
        col.classList.toggle(cls, cls === ratioClass);
      });
    }
  }

  /**
   * Returns the CSS class name for the mobile image aspect ratio given N kit items.
   *  0-3  -> 3:1  (wide strip -- big cells for first few products)
   *  4-6  -> 3:2
   *  7-8  -> 2:1
   *  9-12 -> 4:3
   *  13+  -> 1:1  (square)
   */
  function getMobileRatioClass(n) {
    if (n <= 3)  return 'kb-ratio--3x1';
    if (n <= 6)  return 'kb-ratio--3x2';
    if (n <= 8)  return 'kb-ratio--2x1';
    if (n <= 12) return 'kb-ratio--4x3';
    return 'kb-ratio--1x1';
  }

  /**
   * Update the colour banner below the image frame.
   * Background = metallic gradient sweep (bannerGradient) for the chosen family.
   * isColourDark uses the flat bannerHex (not the gradient string) for the text contrast check.
   */
  function updateColourBanner() {
    var banner = document.getElementById('kb-colour-banner');
    if (!banner) return;

    var hasColour = !!state.selectedColour;
    banner.hidden = !hasColour;

    if (hasColour) {
      var family = getFamilyByKey(state.selectedColour);
      var flatBg = family ? family.bannerHex : colourNameToCSS(state.selectedColour);
      var bg     = family ? (family.bannerGradient || flatBg) : flatBg;
      banner.style.background = bg;
      // Use flat hex for dark-check -- gradient strings can't be parsed by isColourDark
      banner.style.color = isColourDark(flatBg) ? '#fff' : '#111';

      // Drive the completed-step dot to match the chosen swatch's metallic gradient.
      // --kb-accent is consumed by CSS on .kit-builder__step-indicator.is-completed
      var kbEl = document.querySelector('.kit-builder');
      if (kbEl) kbEl.style.setProperty('--kb-accent', bg); // bg = gradient (or flat hex fallback)
    }
  }

  // -- MOSAIC ----------------------------------------------------------------

  /**
   * Returns the mosaic grid column count for N kit items.
   *
   * On mobile (< 768px) the columns are matched to the frame's aspect ratio
   * so that each cell stays approximately square:
   *   n <= 3  -> 3 cols  (3:1 frame: items 1/2/3 each occupy exactly one third)
   *   n <= 6  -> 3 cols  (3:2 frame: 3x2 grid = square cells)
   *   n <= 8  -> 4 cols  (2:1 frame: 4x2 grid = square cells)
   *   n <= 12 -> 4 cols  (4:3 frame: 4x3 grid = square cells)
   *   n > 12 -> sqrt-based (1:1 frame)
   *
   * On desktop the original square-root-based logic is used unchanged.
   */
  function getOptimalCols(n) {
    if (window.innerWidth < 768) {
      if (n <= 3)  return 3;
      if (n <= 6)  return 3;
      if (n <= 12) return 4;
      return Math.min(5, Math.ceil(Math.sqrt(n)));
    }
    // Desktop
    if (n <= 1)  return 1;
    if (n <= 4)  return 2;
    if (n <= 9)  return 3;
    if (n <= 16) return 4;
    return Math.min(5, Math.ceil(Math.sqrt(n)));
  }

  function renderMosaic() {
    var container = document.getElementById('kb-mosaic');
    if (!container) return;

    // Only main items (not addon icon/logo) appear in the mosaic
    var mainItems = state.kitItems.filter(function (i) { return !i.isAddon; });

    if (mainItems.length === 0) {
      container.removeAttribute('style');
      container.innerHTML = '';
      return;
    }

    var GAP = 5;
    var cols = getOptimalCols(mainItems.length);

    container.style.gridTemplateColumns = 'repeat(' + cols + ', 1fr)';
    container.style.gap = GAP + 'px';
    container.style.padding = GAP + 'px';
    container.style.alignContent = 'start';

    container.innerHTML = mainItems.map(function (item) {
      var img = item.variant.image || item.product.image;
      return (
        '<div class="kit-builder__preview-item">' +
          '<img src="' + esc(img) + '" alt="' + esc(item.product.title) + '" loading="lazy">' +
          (item.qty > 1
            ? '<span class="kit-builder__preview-qty" aria-label="Quantity: ' + item.qty + '">&times;' + item.qty + '</span>'
            : '') +
        '</div>'
      );
    }).join('');
  }

  // -- PERSONALISE STEP ------------------------------------------------------

  function renderFonts() {
    var grid = document.getElementById('kb-font-grid');
    if (!grid) return;

    grid.innerHTML = window.KitBuilder.fonts.map(function (font) {
      var active = font.id === state.engraving.fontId;
      // Style goes on an inner <span> so theme CSS targeting <button> font-family
      // (often set with !important) cannot override the custom font display.
      var spanStyle = [
        'font-family:' + font.family + ' !important',
        'font-style:' + font.style + ' !important',
        'font-weight:' + font.weight + ' !important',
        'font-size:1.15em',
        'display:block'
      ];
      if (font.id === 'stereo') { spanStyle.push('text-transform:uppercase'); }
      return (
        '<button ' +
          'class="kit-builder__font-btn' + (active ? ' is-selected' : '') + '" ' +
          'data-font-id="' + esc(font.id) + '" ' +
          'role="radio" ' +
          'aria-checked="' + (active ? 'true' : 'false') + '">' +
          '<span style="' + spanStyle.join(';') + '">' + esc(font.label) + '</span>' +
        '</button>'
      );
    }).join('');

    grid.querySelectorAll('.kit-builder__font-btn').forEach(function (btn) {
      btn.addEventListener('click', function () {
        state.engraving.fontId = this.dataset.fontId;
        grid.querySelectorAll('.kit-builder__font-btn').forEach(function (b) {
          b.classList.remove('is-selected');
          b.setAttribute('aria-checked', 'false');
        });
        this.classList.add('is-selected');
        this.setAttribute('aria-checked', 'true');
        // Mirror uppercase transform on the input so customer sees caps as they type
        var input = document.getElementById('kb-engraving-text');
        if (input) { input.style.textTransform = getFontById(this.dataset.fontId).id === 'stereo' ? 'uppercase' : ''; }
        refreshEngravedBanner();
      });
    });
    // Apply correct transform for the initial/current font selection
    var inputEl = document.getElementById('kb-engraving-text');
    if (inputEl) { inputEl.style.textTransform = getFontById(state.engraving.fontId).id === 'stereo' ? 'uppercase' : ''; }
  }

  /**
   * Renders the icon picker grid from kit-icon products.
   * Shows icon image on dark background, title, price. One selectable at a time.
   * If no icon products exist, hides the field entirely.
   */
  function renderIconPicker() {
    var field = document.getElementById('kb-icon-field');
    var grid  = document.getElementById('kb-icon-grid');
    if (!field || !grid) return;

    var iconProducts = getIconProducts();
    if (iconProducts.length === 0) {
      field.style.display = 'none';
      return;
    }

    field.style.display = '';

    // Match icon card background to the selected colour banner
    var currentFamily = getFamilyByKey(state.selectedColour);
    var cardBg = currentFamily ? currentFamily.bannerHex : '#1a1a1a';

    grid.innerHTML = iconProducts.map(function (product) {
      var variant    = getDefaultVariant(product);
      var isActive   = state.engraving.iconProductId === product.id;

      // Resolve icon image: prefer theme asset (svg then png), fall back to product image.
      var assetEntry = window.KitBuilder.settings.iconAssetUrls
        ? window.KitBuilder.settings.iconAssetUrls[product.id]
        : null;
      var imgSrc, imgExtras;
      if (assetEntry) {
        imgSrc    = assetEntry.svg;
        imgExtras = ' data-icon-chain="' + esc(assetEntry.png) + '|' + esc(variant.image || product.image || '') + '"' +
                    ' onerror="window._kbIconFallback(this)"';
      } else {
        imgSrc    = variant.image || product.image;
        imgExtras = '';
      }

      return (
        '<button ' +
          'class="kit-builder__icon-card' + (isActive ? ' is-selected' : '') + '" ' +
          'data-product-id="' + product.id + '" ' +
          'data-variant-id="' + variant.id + '" ' +
          'role="radio" ' +
          'aria-checked="' + (isActive ? 'true' : 'false') + '" ' +
          'aria-label="' + esc(product.title) + '" ' +
          'type="button" ' +
          'style="background:' + esc(cardBg) + '">' +
          '<div class="kit-builder__icon-img-wrap">' +
            '<img src="' + esc(imgSrc) + '"' + imgExtras + ' alt="' + esc(product.image_alt) + '" loading="lazy">' +
          '</div>' +
          '<p class="kit-builder__icon-price">' + esc(variant.price_formatted) + '</p>' +
        '</button>'
      );
    }).join('');

    grid.querySelectorAll('.kit-builder__icon-card').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var productId = parseInt(this.dataset.productId, 10);
        var variantId = parseInt(this.dataset.variantId, 10);
        selectIcon(productId, variantId);
      });
    });
  }

  /**
   * Selects an icon. Clicking the same icon again deselects it.
   * The icon is added to kitItems with isAddon:true (excluded from mosaic/total display
   * in the main kit, but included in cart payload).
   */
  function selectIcon(productId, variantId) {
    // Toggle off if already selected
    if (state.engraving.iconProductId === productId) {
      state.engraving.iconProductId = null;
      state.engraving.iconVariantId = null;
      // Remove from kitItems
      state.kitItems = state.kitItems.filter(function (i) {
        return !(i.isAddon && i.product.id === productId);
      });
    } else {
      // Deselect any previously selected icon
      if (state.engraving.iconProductId) {
        state.kitItems = state.kitItems.filter(function (i) {
          return !(i.isAddon && hasTag(i.product, 'kit-icon'));
        });
      }
      var product = window.KitBuilder.products.find(function (p) { return p.id === productId; });
      if (!product) return;
      var variant = product.variants.find(function (v) { return v.id === variantId; });
      if (!variant) return;

      state.engraving.iconProductId = productId;
      state.engraving.iconVariantId = variantId;

      state.kitItems.push({
        product: product,
        variant: variant,
        qty: 1,
        priceFormatted: variant.price_formatted,
        priceCents: variant.price_cents,
        isAddon: true
      });
    }

    // Update icon card selection state
    var grid = document.getElementById('kb-icon-grid');
    if (grid) {
      grid.querySelectorAll('.kit-builder__icon-card').forEach(function (btn) {
        var active = parseInt(btn.dataset.productId, 10) === state.engraving.iconProductId;
        btn.classList.toggle('is-selected', active);
        btn.setAttribute('aria-checked', active ? 'true' : 'false');
      });
    }

    updateNextButton();
    refreshEngravedBanner();
  }

  /**
   * Renders the logo uploader from the kit-logo-upload product.
   * Shows price and file input. If no logo product exists, hides the field.
   */
  function renderLogoUploader() {
    var field     = document.getElementById('kb-logo-field');
    var priceEl   = document.getElementById('kb-logo-price-label');
    if (!field) return;

    var logoProd = getLogoProduct();
    if (!logoProd) {
      field.style.display = 'none';
      return;
    }

    field.style.display = '';
    var variant = getDefaultVariant(logoProd);
    // Description text is set in the Liquid -- do not override it here.

    // Bind file input (only once -- check for flag)
    var fileInput = document.getElementById('kb-logo-file-input');
    var removeBtn = document.getElementById('kb-logo-remove-btn');
    if (fileInput && !fileInput._kbBound) {
      fileInput._kbBound = true;
      fileInput.addEventListener('change', function () {
        if (this.files && this.files[0]) {
          handleLogoUpload(this.files[0], logoProd, variant);
        }
      });
    }
    if (removeBtn && !removeBtn._kbBound) {
      removeBtn._kbBound = true;
      removeBtn.addEventListener('click', function () {
        removeLogoUpload();
      });
    }

    // Restore UI state if logo already uploaded
    var filenameEl = document.getElementById('kb-logo-upload-filename');
    if (filenameEl) {
      filenameEl.textContent = state.engraving.logoFileName || 'Choose file...';
    }
    if (removeBtn) {
      removeBtn.style.display = state.engraving.logoFileName ? '' : 'none';
    }
  }

  /**
   * Handles a logo file upload:
   * 1. Reads as data URL for the live banner preview (instant).
   * 2. Uploads the file to Cloudinary (background fetch).
   *    - While uploading: shows "Uploading..." and disables the Next button.
   *    - On success: stores the permanent Cloudinary URL in state for the cart.
   *    - On failure: removes the logo and shows a brief error so the customer can retry.
   */
  function handleLogoUpload(file, logoProd, variant) {
    var reader = new FileReader();
    reader.onload = function (e) {
      // Remove any existing logo addon from kit
      state.kitItems = state.kitItems.filter(function (i) {
        return !(i.isAddon && hasTag(i.product, 'kit-logo-upload'));
      });

      state.engraving.logoDataUrl   = e.target.result;
      state.engraving.logoFileName  = file.name;
      state.engraving.logoCloudUrl  = null;
      state.engraving.logoThumbUrl  = null;
      state.engraving.logoUploading = true;
      state.engraving.logoProductId = logoProd.id;
      state.engraving.logoVariantId = variant.id;

      // Generate a small base64 thumbnail for the packing slip.
      // The packing slip renders it via canvas + createImageBitmap (bypasses img-src CSP).
      // PNG preferred for SVG sources (vector logos render cleanly);
      // JPEG q=0.3 for raster sources. Cap at 4000 chars (Shopify property limit).
      // White background fill ensures transparent areas don't become black in JPEG output.
      (function (dataUrl, fileType) {
        var thumbImg = new Image();
        thumbImg.onload = function () {
          var maxW = 80, maxH = 53;
          var w = thumbImg.naturalWidth  || thumbImg.width  || 200;
          var h = thumbImg.naturalHeight || thumbImg.height || 200;
          var ratio = Math.min(maxW / w, maxH / h, 1);
          var canvas = document.createElement('canvas');
          canvas.width  = Math.round(w * ratio) || 1;
          canvas.height = Math.round(h * ratio) || 1;
          var ctx = canvas.getContext('2d');
          // White fill so transparent SVGs don't render as black in JPEG
          ctx.fillStyle = '#ffffff';
          ctx.fillRect(0, 0, canvas.width, canvas.height);
          ctx.drawImage(thumbImg, 0, 0, canvas.width, canvas.height);
          var thumb;
          if (fileType === 'image/svg+xml') {
            // PNG is lossless and preserves vector sharpness; fall back to JPEG if too large
            thumb = canvas.toDataURL('image/png');
            if (thumb.length > 4000) {
              thumb = canvas.toDataURL('image/jpeg', 0.4);
            }
          } else {
            thumb = canvas.toDataURL('image/jpeg', 0.3);
          }
          // Only store if small enough for Shopify to accept as a line item property.
          if (thumb && thumb.length <= 4000) {
            state.engraving.logoThumbUrl = thumb;
          }
        };
        thumbImg.src = dataUrl;
      }(e.target.result, file.type));

      state.kitItems.push({
        product: logoProd,
        variant: variant,
        qty: 1,
        priceFormatted: variant.price_formatted,
        priceCents: variant.price_cents,
        isAddon: true
      });

      var filenameEl = document.getElementById('kb-logo-upload-filename');
      var removeBtn  = document.getElementById('kb-logo-remove-btn');
      if (filenameEl) filenameEl.textContent = 'Uploading...';
      if (removeBtn)  removeBtn.style.display = '';

      updateNextButton();   // disables Next while uploading
      refreshEngravedBanner();

      // Upload to Cloudinary (unsigned preset -- no secret in JS)
      var formData = new FormData();
      formData.append('file', file);
      formData.append('upload_preset', CLOUDINARY_PRESET);

      fetch(CLOUDINARY_UPLOAD_URL, { method: 'POST', body: formData })
        .then(function (res) {
          if (!res.ok) throw new Error('Upload failed (' + res.status + ')');
          return res.json();
        })
        .then(function (data) {
          state.engraving.logoCloudUrl  = data.secure_url;
          state.engraving.logoUploading = false;
          if (filenameEl) filenameEl.textContent = file.name;
          updateNextButton();  // re-enable Next now upload is done

          // Background: ask Worker to upload logo to Shopify CDN so it can render
          // in Order Printer packing slips (cdn.shopify.com is in the CSP allowlist).
          // Non-blocking -- cart submission works whether or not this completes in time.
          if (LOGO_CDN_WORKER_URL) {
            state.engraving.logoCdnUploading = true;
            var cdnPayload = JSON.stringify({
              cloudinaryUrl: data.secure_url,
              filename: file.name
            });
            fetch(LOGO_CDN_WORKER_URL, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: cdnPayload
            })
              .then(function (r) { return r.json(); })
              .then(function (d) {
                if (d && d.cdnUrl) {
                  state.engraving.logoCdnUrl = d.cdnUrl;
                }
              })
              .catch(function (err) {
                console.warn('[KitBuilder] CDN worker upload failed:', err);
              })
              .then(function () {
                // always clear the uploading flag (finally-equivalent in ES5 chain)
                state.engraving.logoCdnUploading = false;
              });
          }
        })
        .catch(function () {
          // Upload failed -- remove logo so customer can retry cleanly
          removeLogoUpload();
          if (filenameEl) filenameEl.textContent = 'Upload failed -- please try again';
          setTimeout(function () {
            if (filenameEl) filenameEl.textContent = 'Choose file...';
          }, 3000);
        });
    };
    reader.readAsDataURL(file);
  }

  /**
   * Removes the uploaded logo from state and kitItems.
   */
  function removeLogoUpload() {
    state.engraving.logoDataUrl      = null;
    state.engraving.logoFileName     = null;
    state.engraving.logoCloudUrl     = null;
    state.engraving.logoThumbUrl     = null;
    state.engraving.logoUploading    = false;
    state.engraving.logoCdnUrl       = null;
    state.engraving.logoCdnUploading = false;
    state.engraving.logoProductId    = null;
    state.engraving.logoVariantId    = null;

    state.kitItems = state.kitItems.filter(function (i) {
      return !(i.isAddon && hasTag(i.product, 'kit-logo-upload'));
    });

    var filenameEl = document.getElementById('kb-logo-upload-filename');
    var removeBtn  = document.getElementById('kb-logo-remove-btn');
    var fileInput  = document.getElementById('kb-logo-file-input');
    if (filenameEl) filenameEl.textContent = 'Choose file...';
    if (removeBtn)  removeBtn.style.display = 'none';
    if (fileInput)  fileInput.value = '';

    updateNextButton();
    refreshEngravedBanner();
  }

  /**
   * Updates the colour banner to show: icon | text (scaled) | logo.
   * - Icon: white-on-black PNG displayed with mix-blend-mode:screen (black drops away).
   * - Text: font-size reduced as character count increases (safe because the banner
   *   has contain:layout style in CSS, so internal reflows never reach the image frame).
   * - Logo: uploaded image data URL shown in right slot.
   * All three slots can coexist independently.
   */
  /**
   * Measure text at a 100px reference size using an offscreen canvas, then scale
   * to fill targetWidth at 93% (leaving ~4% breathing room each side).
   * Returns a px string, e.g. "21.4px".
   * Returns null if the font is not yet loaded (canvas falls back to sans-serif
   * and measurement width would be unreliably short for custom fonts).
   */
  function calcFillFontSize(text, font, targetWidth) {
    if (!calcFillFontSize._canvas) {
      calcFillFontSize._canvas = document.createElement('canvas');
    }
    var ctx = calcFillFontSize._canvas.getContext('2d');
    var REF = 100; // reference px size
    ctx.font = font.style + ' ' + font.weight + ' ' + REF + 'px ' + font.family;
    var w = ctx.measureText(text).width;
    // w < 20 at 100px almost certainly means the custom font hasn't loaded yet
    if (!w || w < 20) return null;
    var px = (targetWidth * 0.93 / w) * REF;
    px = Math.min(px, 48); // cap at 3rem -- avoids huge text on short strings
    px = Math.max(px, 8);  // floor at 0.5rem -- remains legible at max chars
    return px.toFixed(1) + 'px';
  }

  function refreshEngravedBanner() {
    var isPlaceholder = !state.engraving.text;
    // Placeholder always shows in Stereo Gothic (the Oui Chef house font).
    // Once the customer types their own text, switch to their chosen font.
    var font = isPlaceholder
      ? { id: 'stereo', family: '"Stereo Gothic", sans-serif', weight: '400', style: 'normal' }
      : getFontById(state.engraving.fontId);
    var text = state.engraving.text || 'YOUR ENGRAVING';

    // -- Dynamic font-size: measure text with canvas, scale to fill cols 7-18 --
    // slotEl.offsetWidth reflects the CSS grid column width (12/24 of banner),
    // so this automatically adapts to any screen width.
    var textEl = document.getElementById('kb-mosaic-banner-text');
    var slotWidth = (textEl && textEl.offsetWidth > 0) ? textEl.offsetWidth : 180;

    var fontSize = calcFillFontSize(text, font, slotWidth);
    if (!fontSize) {
      // Custom font not loaded yet -- use a safe fallback and re-render once ready
      fontSize = '1.1rem';
      if (document.fonts && document.fonts.ready) {
        document.fonts.ready.then(function () { refreshEngravedBanner(); });
      }
    }

    // Always use the lighter silver colour regardless of banner background.
    var textColour = '#F0F0F0';

    // Font properties only -- no line-height or layout overrides.
    // Vertical centring is handled by CSS (display:flex + align-items:center).
    // Per-font tweaks:
    //   stereo  (Stereo Gothic / OUI CHEF) -- high baseline; padding-top nudges text down
    //   angello (Angello cursive)           -- tighten tracking to keep it cursive
    var fontStyleParts = [
      'font-family:' + font.family,
      'font-style:' + font.style,
      'font-weight:' + font.weight,
      'color:' + textColour,
      'font-size:' + fontSize
    ];
    if (font.id === 'stereo') { fontStyleParts.push('padding-top:0.25em'); fontStyleParts.push('text-transform:uppercase'); }
    if (font.id === 'angello') { fontStyleParts.push('letter-spacing:-0.03em'); }
    var fontStyle = fontStyleParts.join(';');

    var textEl = document.getElementById('kb-mosaic-banner-text');
    if (textEl) {
      textEl.textContent = text;
      // Only update inline styles when they actually change -- avoids style recalc on every keystroke
      if (textEl._kbLastStyle !== fontStyle) {
        textEl._kbLastStyle = fontStyle;
        textEl.style.cssText = fontStyle;
      }
    }

    // -- Icon slot --
    // The container (.kb-banner-icon) stretches to fill the full 4rem banner row via
    // CSS align-self:stretch -- same reference frame as the text container.
    // The IMG inside is sized to font-size height (not the container) so it matches
    // the visual scale of the text. Both containers use flex align-items:center, so
    // both content centres land at exactly 50% of the 4rem row.
    var iconEl = document.getElementById('kb-banner-icon');
    if (iconEl) {
      // Guard: only rebuild innerHTML when the selected icon changes, NOT on every keystroke.
      var newIconId = state.engraving.iconProductId || null;
      if (iconEl._kbLastIconId !== newIconId) {
        iconEl._kbLastIconId = newIconId;
        if (newIconId) {
          var iconProduct = window.KitBuilder.products.find(function (p) {
            return p.id === newIconId;
          });
          if (iconProduct) {
            var iconVariant = iconProduct.variants.find(function (v) {
              return v.id === state.engraving.iconVariantId;
            }) || iconProduct.variants[0];

            // Resolve icon src: prefer theme asset (svg then png), fall back to product image.
            var iconAssetEntry = window.KitBuilder.settings.iconAssetUrls
              ? window.KitBuilder.settings.iconAssetUrls[iconProduct.id]
              : null;
            var iconImgSrc, iconImgExtras;
            if (iconAssetEntry) {
              iconImgSrc    = iconAssetEntry.svg;
              iconImgExtras = ' data-icon-chain="' + esc(iconAssetEntry.png) + '|' + esc(iconVariant.image || iconProduct.image || '') + '"' +
                              ' onerror="window._kbIconFallback(this)"';
            } else {
              iconImgSrc    = iconVariant.image || iconProduct.image;
              iconImgExtras = '';
            }
            iconEl.innerHTML = '<img src="' + esc(iconImgSrc) + '"' + iconImgExtras + ' alt="' + esc(iconProduct.title) + '" class="kb-banner-icon-img">';
          } else {
            iconEl.innerHTML = '';
          }
        } else {
          iconEl.innerHTML = '';
        }
      }
      // Always sync the img height to the current font-size (runs on every text change).
      // Width auto so the SVG aspect ratio is preserved.
      var iconImg = iconEl.querySelector('.kb-banner-icon-img');
      if (iconImg && fontSize) {
        iconImg.style.height = fontSize;
        iconImg.style.width  = 'auto';
      }
    }

    // -- Logo slot --
    var logoEl = document.getElementById('kb-banner-logo');
    if (logoEl) {
      var newLogoUrl = state.engraving.logoDataUrl || null;
      if (logoEl._kbLastLogoUrl !== newLogoUrl) {
        logoEl._kbLastLogoUrl = newLogoUrl;
        if (newLogoUrl) {
          logoEl.innerHTML = '<img src="' + esc(newLogoUrl) + '" alt="Your logo" class="kb-banner-logo-img">';
        } else {
          logoEl.innerHTML = '';
        }
      }
    }
  }

  function getFontById(id) {
    return (
      window.KitBuilder.fonts.find(function (f) { return f.id === id; }) ||
      window.KitBuilder.fonts[0]
    );
  }

  // -- REVIEW STEP -----------------------------------------------------------

  function renderReview() {
    var list    = document.getElementById('kb-review-list');
    var totalEl = document.getElementById('kb-review-total');
    if (!list) return;

    list.innerHTML = state.kitItems.map(function (item) {
      // For the logo addon, show the customer's actual uploaded image instead of
      // the product's own (missing) image. Cloud URL preferred; dataUrl as fallback.
      var isLogoAddon = item.isAddon && hasTag(item.product, 'kit-logo-upload');
      var img = isLogoAddon
        ? (state.engraving.logoCloudUrl || state.engraving.logoDataUrl || '')
        : (item.variant.image || item.product.image);
      var varTitle  = item.variant.title !== 'Default Title' ? item.variant.title : '';
      var lineTotal = formatMoney(item.priceCents * item.qty);
      var addonNote = item.isAddon ? ' <span class="kit-builder__review-addon">(add-on)</span>' : '';

      return (
        '<div class="kit-builder__review-item">' +
          (img
            ? '<img src="' + esc(img) + '" alt="' + esc(item.product.title) + '" width="60" height="60" loading="lazy">'
            : '<div class="kit-builder__review-img-placeholder" aria-hidden="true"></div>') +
          '<div class="kit-builder__review-info">' +
            '<p class="kit-builder__review-title">' + esc(item.product.title) + addonNote + '</p>' +
            (varTitle ? '<p class="kit-builder__review-variant">' + esc(varTitle) + '</p>' : '') +
            '<p class="kit-builder__review-price">' +
              'Qty ' + item.qty + ' &times; ' + esc(item.priceFormatted) +
              ' = <strong>' + esc(lineTotal) + '</strong>' +
            '</p>' +
          '</div>' +
        '</div>'
      );
    }).join('');

    if (totalEl) totalEl.textContent = formatTotal();

    var addBtn     = document.getElementById('kb-add-to-cart');
    var successEl  = document.getElementById('kb-success');
    var anotherBtn = document.getElementById('kb-build-another');
    var btnText = addBtn && addBtn.querySelector('.kb-btn-text');
    var btnLoad = addBtn && addBtn.querySelector('.kb-btn-loading');
    // Reset button to pristine "Add kit to cart" state.
    // Also remove Focal theme's own .is-added class which the theme JS can stamp
    // on the button when cart events fire -- it turns buttons into tiny pill circles.
    if (addBtn) {
      addBtn.disabled = false;
      addBtn.classList.remove('kb-is-added');
      addBtn.classList.remove('is-added');
    }
    if (btnText) { btnText.hidden = false; btnText.textContent = 'Add kit to cart'; }
    if (btnLoad) { btnLoad.hidden = true; }
    if (successEl)  successEl.hidden = true;
    if (anotherBtn) anotherBtn.hidden = true;
  }

  // -- CART ------------------------------------------------------------------

  function addKitToCart() {
    if (state.adding || state.kitItems.length === 0) return;
    state.adding = true;

    var addBtn  = document.getElementById('kb-add-to-cart');
    var btnText = addBtn && addBtn.querySelector('.kb-btn-text');
    var btnLoad = addBtn && addBtn.querySelector('.kb-btn-loading');
    if (addBtn)  addBtn.disabled = true;
    if (btnText) btnText.hidden = true;
    if (btnLoad) btnLoad.hidden = false;

    var font  = getFontById(state.engraving.fontId);
    var items = state.kitItems.map(function (item) {
      var props = {
        '_kit_id':        state.kitId,
        '_kit_colour':    state.selectedColour,
        '_kit_engraving': font.id === 'stereo' ? state.engraving.text.toUpperCase() : state.engraving.text,
        '_kit_font':      font.label
      };
      // Mark icon and logo addon items so the packing slip can identify them.
      // Use product ID match (not tag lookup) -- reliable at cart time regardless of tag state.
      if (item.isAddon) {
        if (state.engraving.iconProductId && item.product.id === state.engraving.iconProductId) {
          props['_kit_addon'] = 'icon';
        } else if (state.engraving.logoProductId && item.product.id === state.engraving.logoProductId) {
          props['_kit_addon'] = 'logo';
        }
      }
      // Add icon reference on all items if an icon was chosen
      if (state.engraving.iconVariantId) {
        props['_kit_icon'] = item.product.title + ' (icon)';
      }
      // Add logo filename, Cloudinary URL, and base64 thumbnail if a logo was uploaded
      if (state.engraving.logoFileName) {
        props['_kit_logo_file'] = state.engraving.logoFileName;
      }
      if (state.engraving.logoCloudUrl) {
        props['_kit_logo_url'] = state.engraving.logoCloudUrl;
      }
      // CDN URL (cdn.shopify.com) -- used by packing slip <img src> to bypass CSP.
      // Only set if the background Worker upload completed before cart submission.
      if (state.engraving.logoCdnUrl) {
        props['_kit_logo_cdn_url'] = state.engraving.logoCdnUrl;
      }
      // Stamp thumbnail only on the logo item itself to keep property size manageable.
      // Double-underscore prefix hides it from customer-facing views and invoice templates.
      if (state.engraving.logoThumbUrl && state.engraving.logoProductId && item.product.id === state.engraving.logoProductId) {
        props['__kit_logo_thumb'] = state.engraving.logoThumbUrl;
      }
      return {
        id: item.variant.id,
        quantity: item.qty,
        properties: props
      };
    });

    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: JSON.stringify({ items: items })
    })
    .then(function (res) {
      if (!res.ok) {
        return res.json().then(function (data) {
          throw new Error(data.description || 'Failed to add to cart');
        });
      }
      return res.json();
    })
    .then(function () {
      // Stamp cart attribute so Flow can tag this as a kit builder order.
      // Fire-and-forget -- don't block the UI on this.
      fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: JSON.stringify({ attributes: { 'Kit Builder Order': 'true' } })
      }).catch(function (e) { console.warn('[KitBuilder] Could not stamp cart attribute:', e); });

      state.adding = false;
      // Show "Added to cart" green state -- avoid addBtn.hidden (Focal overrides [hidden])
      // Use kb-is-added NOT is-added -- Focal theme's is-added rule shrinks buttons to circles
      if (addBtn) {
        addBtn.disabled = true;
        addBtn.classList.add('kb-is-added');
      }
      if (btnLoad) btnLoad.style.display = 'none';
      if (btnText) {
        btnText.style.display = '';
        btnText.textContent = 'Added to cart';
      }
      var successEl  = document.getElementById('kb-success');
      var anotherBtn = document.getElementById('kb-build-another');
      if (successEl)  successEl.hidden = false;
      if (anotherBtn) anotherBtn.hidden = false;
      refreshFocalCart();
    })
    .catch(function (err) {
      state.adding = false;
      if (addBtn)  addBtn.disabled = false;
      if (btnText) btnText.hidden = false;
      if (btnLoad) btnLoad.hidden = true;
      alert(err.message || 'Sorry, something went wrong. Please try again.');
      console.error('[KitBuilder] Cart error:', err);
    });
  }

  /*
   * refreshFocalCart -- v4.6 -- 14:45 2026 06 05
   *
   * Strategy:
   *  1. Fetch /cart.js for the authoritative item_count (includes ALL items, not just kit).
   *  2. Re-render mini-cart drawer contents via /?sections=mini-cart.
   *  3. Re-render the header cart icon bubble via /?sections= (tries multiple Focal section
   *     names: cart-icon-bubble, cart-notification-button, cart-notification).
   *  4. After BOTH section re-renders resolve, stamp the count onto every known badge
   *     selector as a final authoritative override -- this prevents the async section
   *     re-renders from clobbering each other or a previous manual update.
   *  5. Dispatch native Focal/Shopify cart events so the theme's own JS can react.
   *  6. Open the cart drawer if not already open.
   */
  function refreshFocalCart() {
    fetch('/cart.js', { credentials: 'same-origin' })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        var count    = cart.item_count;  // total items across ALL cart lines
        var countStr = String(count);

        // -- Open cart drawer if closed ----------------------------------------
        var drawer = document.getElementById('mini-cart') || document.querySelector('cart-drawer');
        if (drawer && !drawer.open) {
          var cartLink = document.querySelector('a.header__icon-wrapper[href*="/cart"]') ||
                         document.querySelector('a.tap-area[href*="/cart"]') ||
                         document.querySelector('header a[href*="/cart"]');
          if (cartLink) { try { cartLink.click(); } catch (e) {} }
        }

        // -- Re-render mini-cart drawer body ------------------------------------
        var miniCartP = fetch('/?sections=mini-cart', { credentials: 'same-origin' })
          .then(function (r) { return r.json(); })
          .then(function (data) {
            var sectionHtml = data && data['mini-cart'];
            if (!sectionHtml) return;
            var tmp = document.createElement('div');
            tmp.innerHTML = sectionHtml;

            var newForm  = tmp.querySelector('#mini-cart-form');
            var liveForm = document.querySelector('#mini-cart-form');

            if (newForm && liveForm) {
              liveForm.innerHTML = newForm.innerHTML;
              ['.mini-cart__drawer-footer', '.drawer__footer', '.mini-cart__footer',
               '.cart-drawer__footer', '.mini-cart__subtotal', '.cart-subtotal',
               '[data-cart-total]', '.totals'].forEach(function (sel) {
                var newEl  = tmp.querySelector(sel);
                var liveEl = document.querySelector(sel);
                if (newEl && liveEl) liveEl.innerHTML = newEl.innerHTML;
              });
            } else {
              var liveDrawer2 = document.getElementById('mini-cart') || document.querySelector('cart-drawer');
              if (!liveDrawer2) return;
              var liveInner = null, newInner = null;
              ['.mini-cart__inner', '.cart-drawer__inner', '[data-mini-cart-content]'].forEach(function (sel) {
                if (!liveInner) { liveInner = liveDrawer2.querySelector(sel); newInner = tmp.querySelector(sel); }
              });
              if (liveInner && newInner) {
                liveInner.innerHTML = newInner.innerHTML;
              } else {
                var nd = tmp.querySelector('cart-drawer, #mini-cart');
                if (nd) liveDrawer2.innerHTML = nd.innerHTML;
              }
            }
          }).catch(function () {});

        // -- Re-render header cart icon bubble ---------------------------------
        // Focal may use any of these section names; try all in parallel and
        // use the first one that returns HTML.
        var bubbleNames = ['cart-icon-bubble', 'cart-notification-button', 'cart-notification'];
        var bubbleP = Promise.all(
          bubbleNames.map(function (name) {
            return fetch('/?sections=' + name, { credentials: 'same-origin' })
              .then(function (r) { return r.json(); })
              .then(function (d) { return (d && (d[name] || null)); })
              .catch(function () { return null; });
          })
        ).then(function (results) {
          var html = results.find(function (r) { return !!r; });
          if (!html) return;
          var doc = new DOMParser().parseFromString(html, 'text/html');
          ['#cart-icon-bubble', '.cart-count-bubble', 'cart-notification'].forEach(function (sel) {
            var newEl = doc.querySelector(sel);
            if (!newEl) return;
            document.querySelectorAll(sel).forEach(function (oldEl) {
              oldEl.innerHTML = newEl.innerHTML;
              oldEl.removeAttribute('hidden');
              oldEl.classList.remove('hidden');
            });
          });
        });

        // -- After BOTH re-renders, stamp count onto every known badge ----------
        // Running last ensures async section HTML doesn't overwrite our value.
        Promise.all([miniCartP, bubbleP]).then(function () {
          ['.cart-count-bubble', '#cart-icon-bubble', '[data-cart-count]',
           '.header__cart-count', '.bubble-count', '.cart-count', '.js-cart-count',
           'cart-notification'].forEach(function (sel) {
            document.querySelectorAll(sel).forEach(function (el) {
              var inner = el.querySelector('[aria-hidden="true"]') ||
                          el.querySelector('.visually-hidden + span') ||
                          el.querySelector('span:not(.visually-hidden)');
              if (inner) { inner.textContent = countStr; } else { el.textContent = countStr; }
              el.removeAttribute('hidden');
              el.classList.remove('hidden');
            });
          });
        });

        // -- Dispatch Focal/Shopify cart events so theme JS can react ----------
        document.dispatchEvent(new CustomEvent('cart:refresh',  { bubbles: true }));
        document.dispatchEvent(new CustomEvent('cart:updated',  { bubbles: true, detail: { cart: cart } }));
        document.dispatchEvent(new CustomEvent('theme:cart:updated', { bubbles: true, detail: { cart: cart } }));

      }).catch(function () {});
  }

  function resetForNewKit() {
    state.kitItems = [];
    state.pendingVariantSelections = {};
    state.activePillVariantPerProduct = {};
    state.engraving = {
      text: '',
      fontId: 'serif',
      iconProductId: null,
      iconVariantId: null,
      logoDataUrl: null,
      logoFileName: null,
      logoProductId: null,
      logoVariantId: null
    };
    state.kitId = generateKitId();
    state.selectedCategory = getDefaultCategory();

    var input = document.getElementById('kb-engraving-text');
    if (input) input.value = '';
    var counter = document.getElementById('kb-char-count');
    if (counter) counter.textContent = '0';

    // Reset logo uploader UI
    var filenameEl = document.getElementById('kb-logo-upload-filename');
    var removeBtn  = document.getElementById('kb-logo-remove-btn');
    var fileInput  = document.getElementById('kb-logo-file-input');
    if (filenameEl) filenameEl.textContent = 'Choose file...';
    if (removeBtn)  removeBtn.style.display = 'none';
    if (fileInput)  fileInput.value = '';

    // Clear banner immediately so old engraving text doesn't persist
    // until a colour is chosen (which is what would re-trigger the banner).
    refreshEngravedBanner();

    goToStep(1);
  }

  // -- NAVIGATION ------------------------------------------------------------

  function goToStep(step) {
    state.step = step;

    // Track active step on image col so CSS can apply per-step sticky behaviour.
    // Clear any inline top left by the visual-viewport keyboard fix so the CSS
    // rule takes over correctly on steps other than 3.
    var imageCol = document.getElementById('kb-image-col');
    if (imageCol) {
      imageCol.dataset.activeStep = step;
      imageCol.style.removeProperty('top');
    }

    // Update step instruction subtitle
    var instrEl = document.getElementById('kb-step-instruction');
    if (instrEl) instrEl.textContent = STEP_INSTRUCTIONS[step] || '';

    // Update step announcement text
    var annEl = document.getElementById('kb-step-announcement');
    if (annEl) annEl.textContent = STEP_ANNOUNCEMENTS[step] || '';

    [1, 2, 3, 4].forEach(function (n) {
      var panel = document.getElementById('kb-panel-' + n);
      if (panel) panel.hidden = n !== step;
    });

    document.querySelectorAll('.kit-builder__step-indicator').forEach(function (btn) {
      var n = parseInt(btn.dataset.step, 10);
      btn.classList.toggle('is-active', n === step);
      btn.classList.toggle('is-completed', n < step);
      btn.disabled = n > step;
      if (n === step) btn.setAttribute('aria-current', 'step');
      else btn.removeAttribute('aria-current');
    });

    if (step === 3) {
      renderMosaic();
      refreshEngravedBanner();
      renderFonts();
      renderIconPicker();
      renderLogoUploader();
      // Re-measure after layout settles -- on mobile, hiding the image frame
      // and activating fixed changes the banner slot width. Two rAF calls
      // ensure we measure after the browser has painted the new layout.
      requestAnimationFrame(function () {
        requestAnimationFrame(function () {
          refreshEngravedBanner();
          // Measure actual banner height so content-col margin-top is exact,
          // preventing iOS from scrolling to reveal inputs hidden behind banner.
          updateEngravedBannerMargin();
        });
      });
    }
    if (step === 4) {
      renderMosaic();
      renderReview();
    }

    var sectionId = window.KitBuilder.settings.sectionId;
    var el = document.getElementById('kit-builder-' + sectionId);
    if (el) {
      var y = el.getBoundingClientRect().top + window.pageYOffset - 16;
      window.scrollTo({ top: y, behavior: 'smooth' });
    }
  }

  // -- EVENT BINDING --------------------------------------------------------

  function bindGlobalEvents() {
    on('kb-personalise-btn',        'click', function () { goToStep(3); });
    on('kb-personalise-btn-bottom', 'click', function () { goToStep(3); });
    on('kb-back-to-2',              'click', function () { goToStep(2); });
    on('kb-review-btn',             'click', function () { goToStep(4); });
    on('kb-review-btn-top',         'click', function () { goToStep(4); });
    on('kb-back-to-3',              'click', function () { goToStep(3); });
    on('kb-add-to-cart',            'click', addKitToCart);
    on('kb-build-another',          'click', resetForNewKit);

    document.querySelectorAll('.kit-builder__step-indicator').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var n = parseInt(this.dataset.step, 10);
        if (n < state.step) goToStep(n);
      });
    });

    var input   = document.getElementById('kb-engraving-text');
    var counter = document.getElementById('kb-char-count');
    if (input) {
      input.addEventListener('input', function () {
        state.engraving.text = this.value;
        if (counter) counter.textContent = this.value.length;
        refreshEngravedBanner();
      });
    }
  }

  // -- HELPERS ---------------------------------------------------------------

  function on(id, event, handler) {
    var el = document.getElementById(id);
    if (el) el.addEventListener(event, handler);
  }

  function esc(str) {
    if (str == null) return '';
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  function formatMoney(cents) {
    var currency = window.KitBuilder.settings.currency || 'GBP';
    try {
      return new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency: currency,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format(cents / 100);
    } catch (e) {
      return currency + ' ' + (cents / 100).toFixed(2);
    }
  }

  /**
   * Sums all kit items (including addons) for the running total shown in footers.
   */
  function formatTotal() {
    var total = state.kitItems.reduce(function (sum, item) {
      return sum + item.priceCents * item.qty;
    }, 0);
    return formatMoney(total);
  }

  function generateKitId() {
    var chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
    var id = 'KIT-';
    for (var i = 0; i < 6; i++) {
      id += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return id;
  }

  // -- COLOUR HELPERS --------------------------------------------------------

  /**
   * Returns a solid hex colour for a given name.
   * For split colours (e.g. "Blue Top"), returns the accent hex so the
   * personalisation banner uses the accent tone.
   */
  function colourNameToCSS(name) {
    var lower = name.toLowerCase().trim();
    if (SPLIT_COLOURS[lower]) return SPLIT_COLOURS[lower];
    var map = {
      'gold':          '#D4AF30',
      'chrome':        '#C8D8E8',
      'gloss black':   '#1a1a1a',
      'metallic blue': '#0D20CF',
      'copper':        '#C06A18'
    };
    return map[lower] || '#888888';
  }

  /**
   * Returns CSS for the swatch dot background.
   *
   * Split colours (Blue Top, Gold Top, Chrome Top, Copper Top):
   *   Horizontal split -- accent on the top half, metallic silver on the bottom half.
   *   A subtle sheen gradient is layered on top to give both halves a metallic look.
   *   Result: `linear-gradient(sheen), linear-gradient(accent 50%, silver 50%)`
   *
   * Known metallic finishes: METALLIC_GRADIENTS (multi-stop, top-lit sheen).
   *
   * Everything else: flat hex from colourNameToCSS (no sheen needed for non-metallic).
   */
  function colourNameToSwatchCSS(name) {
    var lower = name.toLowerCase().trim();

    // Chrome Top: strong specular highlight -- bright mirror silver, approved look.
    if (lower === 'chrome top') {
      return (
        'radial-gradient(ellipse at 55% 35%, rgba(255,255,255,0.95) 0%, rgba(255,255,255,0.4) 35%, rgba(255,255,255,0.0) 62%), ' +
        'linear-gradient(to right, #a8b4bc 50%, #dce8f0 50%)'
      );
    }

    // Subtle sheen for coloured metallics: gentle highlight that adds depth without
    // washing out gold, copper, blue etc.
    var SOFT_SHEEN = 'radial-gradient(ellipse at 55% 35%, rgba(255,255,255,0.42) 0%, rgba(255,255,255,0.08) 40%, rgba(255,255,255,0.0) 65%), ';

    // Other split colours -- soft sheen over vertical split: accent left, silver right
    if (SPLIT_COLOURS[lower]) {
      return SOFT_SHEEN + 'linear-gradient(to right, ' + SPLIT_COLOURS[lower] + ' 50%, #E0E4E8 50%)';
    }

    // Known metallic finishes -- soft sheen over gradient (gloss black stays matte)
    if (METALLIC_GRADIENTS[lower]) {
      if (lower === 'gloss black') return METALLIC_GRADIENTS[lower];
      return SOFT_SHEEN + METALLIC_GRADIENTS[lower];
    }

    // Everything else -- solid colour
    return colourNameToCSS(name);
  }

  /**
   * Returns true if the given hex colour is perceptually dark (luminance < 0.55).
   * Falls back to false for non-hex values (e.g. gradients won't reach this).
   */
  function isColourDark(cssColor) {
    var hex = cssColor.replace('#', '');
    if (hex.length === 3) hex = hex.split('').map(function (c) { return c + c; }).join('');
    if (hex.length !== 6) return false;
    var r = parseInt(hex.substr(0, 2), 16);
    var g = parseInt(hex.substr(2, 2), 16);
    var b = parseInt(hex.substr(4, 2), 16);
    return (0.299 * r + 0.587 * g + 0.114 * b) / 255 < 0.55;
  }

})();
